import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router";
import {
  ArrowLeft,
  MapPin,
  Search,
  Move,
  Upload,
  X,
  List,
  Loader2,
  Maximize2,
  Crop,
  Map as MapIcon,
  Lock,
  Unlock,
  Sparkles,
  RotateCw,
  RotateCcw,
  Undo2,
  Redo2,
  Save,
  Trash2,
  Shrink,
  Eye,
  EyeOff,
  Layers,
  PenLine,
  DoorOpen,
  LogOut,
  ArrowUpDown,
  ArrowRight,
  Info,
  Coffee,
  MoreHorizontal,
} from "lucide-react";
import { useAdminEvent } from "../hooks/useAdminEvent";
import type { Company, Hall } from "../../types";
import { mapService } from "../../services/mapService";
import { MapViewport, type MapViewportHandle } from "../../components/MapViewport";
import { CompanyAvatar } from "../../components/CompanyAvatar";
import { ApiError, apiRequest, formatApiError } from "../../api/client";
import {
  parseSvgBoardSize,
  parseBoothRectsFromSvg,
  parseLabeledBoothRectsFromSvg,
  planAutoBoothPlacements,
  isCdrFileName,
  sceneHasContent,
  sceneBoothRects,
  mapBoxesThroughContentPad,
  normalizeBoothCode,
  createEmptyFloorplanScene,
  applyUnderlaySvgLayerVisibility,
  resolveUnderlaySvgLayers,
  DEFAULT_UNDERLAY_SVG_LAYERS,
  FLOORPLAN_KIND_STYLE,
  type FloorplanLinkArrow,
  type FloorplanObjectKind,
  type FloorplanScene,
  type FloorplanSceneObject,
  type UnderlaySvgLayerId,
  type UnderlaySvgLayerFlags,
} from "@mvk/shared";
import { FloorplanSceneView } from "../../components/FloorplanSceneView";
import {
  FloorplanOverlayMarkers,
  type MarkerResizeCorner,
} from "../../components/FloorplanOverlayMarkers";
import { PinCardLabels } from "../../components/PinCardLabels";
import { fitPinLabel } from "../../components/pinLabelFit";

type WorkMode = "scheme" | "placement";
type BoardRotation = 0 | 90 | 180 | 270;

/** Technical objects only — exhibitors are company pins, not duplicate booth markers. */
const MARKER_TOOLS: Array<{
  id: string;
  kind: FloorplanObjectKind;
  label: string;
  w: number;
  h: number;
}> = [
  { id: "entrance", kind: "entrance", label: "Вход", w: 5.5, h: 4 },
  { id: "exit", kind: "exit", label: "Выход", w: 5.5, h: 4 },
  { id: "toilet", kind: "toilet", label: "Туалет", w: 5, h: 4 },
  { id: "stairs", kind: "stairs", label: "Лестница", w: 5.5, h: 4 },
  { id: "office", kind: "zone", label: "Офис организаторов", w: 9, h: 5.5 },
  { id: "conference", kind: "zone", label: "Конференц-зал", w: 10, h: 6 },
  { id: "info", kind: "info", label: "Инфо", w: 5, h: 4 },
  { id: "service", kind: "service", label: "Сервис / фойе", w: 5.5, h: 4 },
  { id: "label", kind: "other", label: "Подпись", w: 8, h: 4 },
  {
    id: "event_link",
    kind: "event_link",
    label: "→ Другая выставка",
    w: 12,
    h: 5,
  },
];

function shortEventTitle(title: string): string {
  const t = title.trim();
  if (t.length <= 28) return t;
  return `${t.slice(0, 26).trim()}…`;
}

/** Name only — arrow is drawn as icon; keep title bold on the plaque. */
function eventLinkLabel(title: string, _arrow: FloorplanLinkArrow = "e"): string {
  return shortEventTitle(title);
}

/** Board % → px inside the CSS-rotated frame that MapViewport pans/zooms. */
function boardPctToFramePx(
  xPct: number,
  yPct: number,
  boardW: number,
  boardH: number,
  rotationDeg: BoardRotation,
): { fx: number; fy: number } {
  const lx = (xPct / 100 - 0.5) * boardW;
  const ly = (yPct / 100 - 0.5) * boardH;
  const θ = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(θ);
  const sin = Math.sin(θ);
  const sx = lx * cos - ly * sin;
  const sy = lx * sin + ly * cos;
  const rw = rotationDeg === 90 || rotationDeg === 270 ? boardH : boardW;
  const rh = rotationDeg === 90 || rotationDeg === 270 ? boardW : boardH;
  return { fx: rw / 2 + sx, fy: rh / 2 + sy };
}

function splitExhibitorNames(raw: string | undefined): string[] {
  return String(raw ?? "")
    .split(/\s*[·|\n;]\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function joinExhibitorNames(names: string[]): string {
  return names.map((s) => s.trim()).filter(Boolean).join(" · ");
}

function newMarkerId(kind: string): string {
  return `${kind}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

const PIN_COLORS = [
  "#6366f1", "#ec4899", "#14b8a6", "#f59e0b",
  "#8b5cf6", "#ef4444", "#06b6d4", "#84cc16",
  "#f97316", "#0ea5e9", "#a855f7", "#22c55e",
];

const DEFAULT_PIN_W = 4.2;
const DEFAULT_PIN_H = 3.2;

function svgNativeSize(hall?: Hall | null): { w: number; h: number } {
  // Underlay-first: board matches the Corel SVG page, not a remapped scene.
  if (hall?.floorplanSvg) {
    const s = parseSvgBoardSize(hall.floorplanSvg);
    return { w: s.width, h: s.height };
  }
  if (hall?.floorplanWidth && hall?.floorplanHeight) {
    return { w: hall.floorplanWidth, h: hall.floorplanHeight };
  }
  if (hall?.floorplanScene && sceneHasContent(hall.floorplanScene)) {
    return { w: hall.floorplanScene.width, h: hall.floorplanScene.height };
  }
  return { w: 1000, h: 700 };
}

function boardPixels(native: { w: number; h: number }, scalePct: number) {
  return {
    width: Math.max(200, Math.min(5000, Math.round((native.w * scalePct) / 100))),
    height: Math.max(200, Math.min(5000, Math.round((native.h * scalePct) / 100))),
  };
}

/** Map board-local % from screen pointer (MapViewport zoom + CSS rotate on the board). */
function boardLocalPercent(
  clientX: number,
  clientY: number,
  frameRect: DOMRect,
  boardW: number,
  boardH: number,
  rotationDeg: BoardRotation,
  viewZoom = 1,
): { x: number; y: number } {
  const z = Math.max(0.05, viewZoom);
  const cx = frameRect.left + frameRect.width / 2;
  const cy = frameRect.top + frameRect.height / 2;
  const sx = clientX - cx;
  const sy = clientY - cy;
  const θ = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(θ);
  const sin = Math.sin(θ);
  // Inverse of CSS rotate(θ) (clockwise positive), then undo viewport scale().
  const lx = (sx * cos + sy * sin) / z;
  const ly = (-sx * sin + sy * cos) / z;
  const x = ((lx + boardW / 2) / Math.max(1, boardW)) * 100;
  const y = ((ly + boardH / 2) / Math.max(1, boardH)) * 100;
  return {
    x: Math.min(100, Math.max(0, Number(x.toFixed(2)))),
    y: Math.min(100, Math.max(0, Number(y.toFixed(2)))),
  };
}

type CropInsets = { left: number; top: number; right: number; bottom: number };

function clampCropInsets(c: CropInsets): CropInsets {
  const clamp = (n: number) => Math.max(0, Math.min(40, Number(n) || 0));
  let left = clamp(c.left);
  let right = clamp(c.right);
  let top = clamp(c.top);
  let bottom = clamp(c.bottom);
  if (left + right > 80) {
    const s = 80 / (left + right);
    left *= s;
    right *= s;
  }
  if (top + bottom > 80) {
    const s = 80 / (top + bottom);
    top *= s;
    bottom *= s;
  }
  return {
    left: Number(left.toFixed(1)),
    right: Number(right.toFixed(1)),
    top: Number(top.toFixed(1)),
    bottom: Number(bottom.toFixed(1)),
  };
}

/** Remap a 0–100% coordinate from one crop space to another via the original page. */
function remapPctThroughCrop(
  pct: number,
  fromStart: number,
  fromEnd: number,
  toStart: number,
  toEnd: number,
): number {
  const fromSpan = Math.max(1e-6, 100 - fromStart - fromEnd);
  const toSpan = Math.max(1e-6, 100 - toStart - toEnd);
  const orig = fromStart + (pct / 100) * fromSpan;
  return Number((((orig - toStart) / toSpan) * 100).toFixed(2));
}

/** Extra trim of the *current* board view → absolute crop insets. */
function absoluteCropFromTrim(base: CropInsets, trim: CropInsets): CropInsets {
  const spanX = (100 - base.left - base.right) / 100;
  const spanY = (100 - base.top - base.bottom) / 100;
  return clampCropInsets({
    left: base.left + trim.left * spanX,
    right: base.right + trim.right * spanX,
    top: base.top + trim.top * spanY,
    bottom: base.bottom + trim.bottom * spanY,
  });
}

/** Screen pixel delta → board-local % delta (MapViewport zoom + CSS rotate). */
function screenDeltaToBoardPct(
  dxScreen: number,
  dyScreen: number,
  boardW: number,
  boardH: number,
  rotationDeg: BoardRotation,
  viewZoom = 1,
): { dx: number; dy: number } {
  const z = Math.max(0.05, viewZoom);
  const θ = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(θ);
  const sin = Math.sin(θ);
  const lx = (dxScreen * cos + dyScreen * sin) / z;
  const ly = (-dxScreen * sin + dyScreen * cos) / z;
  return {
    dx: (lx / Math.max(1, boardW)) * 100,
    dy: (ly / Math.max(1, boardH)) * 100,
  };
}

/** PNG/JPG plan → SVG underlay (draw markers by hand on top). */
async function rasterFileToSvgUnderlay(file: File): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error("read_failed"));
    reader.readAsDataURL(file);
  });
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error("image_decode_failed"));
    el.src = dataUrl;
  });
  const maxSide = 2400;
  const w0 = img.naturalWidth || img.width;
  const h0 = img.naturalHeight || img.height;
  if (w0 < 8 || h0 < 8) throw new Error("image_too_small");
  const scale = Math.min(1, maxSide / Math.max(w0, h0));
  const outW = Math.max(8, Math.round(w0 * scale));
  const outH = Math.max(8, Math.round(h0 * scale));
  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas_failed");
  ctx.drawImage(img, 0, 0, outW, outH);
  const href = canvas.toDataURL("image/jpeg", 0.86);
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" ` +
    `width="${outW}" height="${outH}" viewBox="0 0 ${outW} ${outH}">` +
    `<image href="${href}" xlink:href="${href}" width="${outW}" height="${outH}" ` +
    `preserveAspectRatio="none"/></svg>`
  );
}

function rotateEdge(
  edge: "n" | "s" | "e" | "w",
  rotationDeg: BoardRotation,
): "n" | "s" | "e" | "w" {
  const order = ["n", "e", "s", "w"] as const;
  const i = order.indexOf(edge);
  const t = (rotationDeg / 90) % 4;
  return order[(i + t) % 4]!;
}

/**
 * «Поле ←/↑» buttons are screen directions. After CSS board rotate, map to the
 * board-local edge that contentPad understands (otherwise ← expands top).
 */
function screenEdgeToBoardEdge(
  screenEdge: "n" | "s" | "e" | "w",
  boardRotation: BoardRotation,
): "n" | "s" | "e" | "w" {
  const inv = ((360 - boardRotation) % 360) as BoardRotation;
  return rotateEdge(screenEdge, inv);
}

/** CSS cursor for a board-local resize handle after visual rotation. */
function cursorForHandle(
  handle: "se" | "sw" | "ne" | "nw" | "n" | "s" | "e" | "w",
  rotationDeg: BoardRotation,
): string {
  if (handle.length === 1) {
    const s = rotateEdge(handle as "n" | "s" | "e" | "w", rotationDeg);
    return s === "n" || s === "s" ? "ns-resize" : "ew-resize";
  }
  const a = rotateEdge(handle[0] as "n" | "s" | "e" | "w", rotationDeg);
  const b = rotateEdge(handle[1] as "n" | "s" | "e" | "w", rotationDeg);
  const set = new Set([a, b]);
  if ((set.has("n") && set.has("w")) || (set.has("s") && set.has("e"))) {
    return "nwse-resize";
  }
  return "nesw-resize";
}

type PinLabelRot = 0 | 90 | 180 | 270;

function normalizePinLabelRot(raw: number | null | undefined): PinLabelRot {
  const n = Number(raw ?? 0);
  const r = ((Math.round(n / 90) * 90) % 360 + 360) % 360;
  if (r === 90 || r === 180 || r === 270) return r;
  return 0;
}

/** Pin caption: custom mapLabel or company name. Empty override → company name. */
function pinCaption(c: { name: string; mapLabel?: string | null }): string {
  const custom = (c.mapLabel ?? "").trim();
  return custom || c.name;
}

function pinNameLines(raw: number | null | undefined): 1 | 2 | 3 {
  if (raw === 1) return 1;
  if (raw === 3) return 3;
  return 2;
}

function pinEffectiveScale(
  globalScale: number,
  perPin: number | null | undefined,
): number {
  const g = Math.max(0.7, Math.min(2.2, globalScale));
  if (typeof perPin === "number" && perPin > 0) {
    return Math.min(2.5, Math.max(0.5, g * perPin));
  }
  return g;
}

type AdminPinGroup = {
  key: string;
  booth: string;
  companies: Company[];
  left: number;
  top: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
};

function cloneFloorplanScene(scene: FloorplanScene): FloorplanScene {
  return JSON.parse(JSON.stringify(scene)) as FloorplanScene;
}

function isPlaced(c: Company): boolean {
  return c.mapPoint.x > 0 || c.mapPoint.y > 0;
}

function pinColorFor(c: Company, index: number): string {
  if (c.pinColor && /^#[0-9a-fA-F]{6}$/.test(c.pinColor)) return c.pinColor;
  return PIN_COLORS[index % PIN_COLORS.length];
}

function pinSize(c: Company): { w: number; h: number } {
  const w = c.mapPoint.w && c.mapPoint.w > 0 ? c.mapPoint.w : DEFAULT_PIN_W;
  const h = c.mapPoint.h && c.mapPoint.h > 0 ? c.mapPoint.h : DEFAULT_PIN_H;
  return { w, h };
}

function pinBox(c: Company): { left: number; top: number; w: number; h: number } {
  const { w, h } = pinSize(c);
  return {
    left: c.mapPoint.x - w / 2,
    top: c.mapPoint.y - h / 2,
    w,
    h,
  };
}

function boothSnapResult(r: {
  cx: number;
  cy: number;
  w: number;
  h: number;
}): { x: number; y: number; w: number; h: number } {
  return {
    x: Number(r.cx.toFixed(2)),
    y: Number(r.cy.toFixed(2)),
    w: Number(Math.max(1.2, r.w).toFixed(2)),
    h: Number(Math.max(1.2, r.h).toFixed(2)),
  };
}

/**
 * Snap pin to a booth cell.
 * If `preferredCode` is set (e.g. A507), ONLY that labeled cell is used —
 * never the nearest neighbour (that was stacking A507 under A519).
 */
function nearestBoothAt(
  hall: Hall | null | undefined,
  x: number,
  y: number,
  maxDist = 7,
  preferredCode?: string | null,
): { x: number; y: number; w: number; h: number } | null {
  const want = normalizeBoothCode(preferredCode);
  const pad = hall?.floorplanScene?.contentPad;
  // Prefer SVG geometry when underlay exists — scene may be a remapped/tight crop.
  // SVG is underlay-local %; map into board % when contentPad expanded the field.
  const fromSvg = hall?.floorplanSvg
    ? mapBoxesThroughContentPad(parseLabeledBoothRectsFromSvg(hall.floorplanSvg), pad)
    : [];
  const fromScene =
    hall?.floorplanScene && sceneHasContent(hall.floorplanScene)
      ? sceneBoothRects(hall.floorplanScene)
      : [];
  const labeled = fromSvg.length ? fromSvg : fromScene;

  if (want) {
    const match = labeled.find((r) => normalizeBoothCode(r.code) === want);
    if (match) return boothSnapResult(match);
    // Known stand number but no matching cell — do not steal a neighbour's box.
    return null;
  }

  const rects = labeled.length
    ? labeled
    : hall?.floorplanSvg
      ? mapBoxesThroughContentPad(parseBoothRectsFromSvg(hall.floorplanSvg), pad)
      : [];
  if (!rects.length) return null;
  let best: (typeof rects)[number] | null = null;
  let bestScore = Number.POSITIVE_INFINITY;
  for (const r of rects) {
    const inside = x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
    const d = Math.hypot(r.cx - x, r.cy - y);
    const score = inside ? d * 0.15 : d;
    if (score < bestScore) {
      bestScore = score;
      best = r;
    }
  }
  if (!best) return null;
  const inside =
    x >= best.x && x <= best.x + best.w && y >= best.y && y <= best.y + best.h;
  if (!inside && bestScore > maxDist) return null;
  return boothSnapResult(best);
}

/** Prefer hall label/name without duplicated «Павильон Павильон…». */
function preferredHallCaption(h: { name?: string; label?: string }): string {
  const name = (h.name ?? "").trim();
  const label = (h.label ?? "").trim();
  if (label && /павильон|pavilion|зал\b|hall\b/i.test(label)) return label;
  if (name && !/^павильон\s+павильон/i.test(name)) return name || label;
  return label || name;
}

export function AdminMapPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { event, loading: eventLoading } = useAdminEvent(id);
  const [halls, setHalls] = useState<Hall[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadingSvg, setUploadingSvg] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [activeHall, setActiveHall] = useState("");
  const [selectedCompany, setSelectedCompany] = useState<string | null>(
    () => searchParams.get("company"),
  );
  /** Multi-select on the board (Shift+click). Always includes selectedCompany when set. */
  const [selectedIds, setSelectedIds] = useState<string[]>(() => {
    const c = searchParams.get("company");
    return c ? [c] : [];
  });
  const [search, setSearch] = useState("");
  const [listSearch, setListSearch] = useState("");
  /** Scheme editor (underlay + markers) vs exhibitor pin placement. */
  const [workMode, setWorkMode] = useState<WorkMode>("scheme");
  const [editMode, setEditMode] = useState(false);
  const [placeMode, setPlaceMode] = useState(false);
  const [cropMode, setCropMode] = useState(false);
  /** Corel SVG underlay — visual truth 1:1. Default on when SVG exists. */
  const [showUnderlay, setShowUnderlay] = useState(true);
  /** Scheme markers layer. On in scheme mode; optional in placement. */
  const [showOverlay, setShowOverlay] = useState(true);
  /** Exhibitor pins layer (placement). */
  const [showPins, setShowPins] = useState(true);
  /**
   * Legacy booth markers (duplicate of exhibitor pins). Off by default —
   * exhibitors are pins; scheme markers are only technical objects.
   */
  const [showSchemeBooths, setShowSchemeBooths] = useState(false);
  /** Slices inside Corel/LO underlay SVG (grid / black contours / fills…). */
  const [underlayLayers, setUnderlayLayers] = useState<UnderlaySvgLayerFlags>(() => ({
    ...DEFAULT_UNDERLAY_SVG_LAYERS,
  }));
  const [layersOpen, setLayersOpen] = useState(false);
  const layersBtnRef = useRef<HTMLButtonElement>(null);
  const [layersMenuPos, setLayersMenuPos] = useState<{ top: number; right: number } | null>(null);
  /** Active stamp tool in scheme mode (null = select/move). */
  const [markerToolId, setMarkerToolId] = useState<string | null>(null);
  const [selectedMarkerId, setSelectedMarkerId] = useState<string | null>(null);
  const [markerLabelDraft, setMarkerLabelDraft] = useState("");
  const [markerCodeDraft, setMarkerCodeDraft] = useState("");
  /** Sibling exhibitions in the same group (for event_link plaques). */
  const [groupSiblings, setGroupSiblings] = useState<
    Array<{ id: string; title: string; primaryColor?: string }>
  >([]);
  /** Local editable copy of scene overlays. */
  const [sceneDraft, setSceneDraft] = useState<FloorplanScene | null>(null);
  /** Unsaved local scheme edits (explicit Save). */
  const [sceneDirty, setSceneDirty] = useState(false);
  const [undoDepth, setUndoDepth] = useState(0);
  const [redoDepth, setRedoDepth] = useState(0);
  /** Board rotation (90° steps) — CSS + persisted via rotate API when scene exists. */
  const [boardRotation, setBoardRotation] = useState<BoardRotation>(0);
  /** Keep overlay label text upright on screen after board rotate. */
  /** Keep placement pin labels readable after board rotate (расстановка). */
  const [labelsUpright, setLabelsUpright] = useState(true);
  /** 0.7–2.2 — placement pin label size (floorplanScene.labelScale) */
  const [labelScale, setLabelScale] = useState(1.15);
  /** Extra global rotation for all pin labels (floorplanScene.pinLabelRotationDeg). */
  const [pinLabelRotationDeg, setPinLabelRotationDeg] = useState<PinLabelRot>(0);
  const [moreOpen, setMoreOpen] = useState(false);
  const [moreMenuPos, setMoreMenuPos] = useState<{ top: number; right: number } | null>(null);
  const moreBtnRef = useRef<HTMLButtonElement>(null);
  const [mobileListOpen, setMobileListOpen] = useState(false);
  const [boardScale, setBoardScale] = useState(100);
  /** Viewport pinch/wheel zoom — chrome (borders/handles) must stay screen-fixed. */
  const [viewZoom, setViewZoom] = useState(1);
  /** Display % of width-fit (100% = план по ширине рабочей области). */
  const [viewZoomPct, setViewZoomPct] = useState(100);
  const [boothDraft, setBoothDraft] = useState("");
  /** Pin caption draft; empty → company name. */
  const [mapLabelDraft, setMapLabelDraft] = useState("");
  const [mapLabelLinesDraft, setMapLabelLinesDraft] = useState<1 | 2 | 3>(2);
  const [mapLabelScaleDraft, setMapLabelScaleDraft] = useState(1);
  const [colorDraft, setColorDraft] = useState(PIN_COLORS[0]!);
  const [cropDraft, setCropDraft] = useState<CropInsets>({ left: 0, top: 0, right: 0, bottom: 0 });
  /** Extra trim of the currently shown board while editing crop (drag edges). */
  const [cropTrim, setCropTrim] = useState<CropInsets>({ left: 0, top: 0, right: 0, bottom: 0 });
  const cropDrag = useRef<{
    edge: "n" | "s" | "e" | "w" | "nw" | "ne" | "sw" | "se";
    startClientX: number;
    startClientY: number;
    startTrim: CropInsets;
  } | null>(null);
  const cropTrimRef = useRef(cropTrim);
  cropTrimRef.current = cropTrim;
  const cropDraftRef = useRef(cropDraft);
  cropDraftRef.current = cropDraft;
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.matchMedia("(min-width: 768px)").matches : true,
  );

  const svgInputRef = useRef<HTMLInputElement>(null);
  const mapViewportRef = useRef<MapViewportHandle>(null);
  /** Unrotated frame (swapped size when 90/270) — used for hit-testing. */
  const mapFrameRef = useRef<HTMLDivElement>(null);
  const mapSurfaceRef = useRef<HTMLDivElement>(null);
  const boardRotationRef = useRef<BoardRotation>(0);
  const boardSizeRef = useRef({ w: 1000, h: 700 });
  const viewZoomRef = useRef(1);
  /** Pin move: only after threshold — click must not jump the pin under the cursor. */
  const pinDrag = useRef<{
    companyId: string;
    mateIds: string[];
    startClientX: number;
    startClientY: number;
    originX: number;
    originY: number;
    moved: boolean;
  } | null>(null);
  const resizeState = useRef<{
    companyId: string;
    mateIds: string[];
    corner: "se" | "sw" | "ne" | "nw" | "n" | "s" | "e" | "w";
    startClientX: number;
    startClientY: number;
    startLeft: number;
    startTop: number;
    startW: number;
    startH: number;
    moved: boolean;
  } | null>(null);
  const markerDrag = useRef<{
    id: string;
    mode: "move" | "resize";
    corner?: MarkerResizeCorner;
    startX: number;
    startY: number;
    orig: FloorplanSceneObject;
  } | null>(null);
  const sceneDraftRef = useRef<FloorplanScene | null>(null);
  sceneDraftRef.current = sceneDraft;
  const sceneUndoRef = useRef<FloorplanScene[]>([]);
  const sceneRedoRef = useRef<FloorplanScene[]>([]);
  const companiesRef = useRef(companies);
  companiesRef.current = companies;
  const nativeBoardRef = useRef({ w: 1000, h: 700 });
  const persistSceneTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reloadMap = useCallback(async () => {
    if (!id) return;
    const data = await mapService.get(id, "admin");
    setHalls(data.halls);
    setCompanies(data.companies);
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await reloadMap();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, reloadMap]);

  useEffect(() => {
    if (!id) {
      setGroupSiblings([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiRequest<{
          groups: Array<{
            id: string;
            eventIds: string[];
            events: Array<{ id: string; title: string }>;
          }>;
        }>("/event-groups", { audience: "admin" });
        const group = res.groups.find((g) => g.eventIds.includes(id));
        if (cancelled) return;
        const siblings = (group?.events ?? []).filter((e) => e.id !== id);
        const withColors = await Promise.all(
          siblings.map(async (e) => {
            try {
              const { event } = await apiRequest<{
                event: { theme?: { primaryColor?: string } };
              }>(`/events/${e.id}`, { audience: "admin" });
              const raw = (event?.theme?.primaryColor ?? "").trim();
              return {
                id: e.id,
                title: e.title,
                primaryColor: /^#[0-9a-fA-F]{6}$/.test(raw) ? raw : undefined,
              };
            } catch {
              return { id: e.id, title: e.title };
            }
          }),
        );
        if (!cancelled) setGroupSiblings(withColors);
      } catch {
        if (!cancelled) setGroupSiblings([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (halls.length && !activeHall) setActiveHall(halls[0]!.id);
  }, [halls, activeHall]);

  useEffect(() => {
    const companyId = searchParams.get("company");
    if (!companyId || !companies.length) return;
    setSelectedCompany(companyId);
    setSelectedIds([companyId]);
    const match = companies.find((c) => c.id === companyId);
    if (match?.mapPoint?.hallId) setActiveHall(match.mapPoint.hallId);
  }, [searchParams, companies]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.tagName === "SELECT" ||
          t.isContentEditable)
      ) {
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && !e.shiftKey) {
        e.preventDefault();
        undoScene();
        return;
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key.toLowerCase() === "y" || (e.key.toLowerCase() === "z" && e.shiftKey))
      ) {
        e.preventDefault();
        redoScene();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        void saveSceneExplicit();
        return;
      }
      if (
        workMode === "placement" &&
        (e.key === "Delete" || e.key === "Backspace") &&
        selectedMarkerId
      ) {
        e.preventDefault();
        removeSelectedMarker();
        return;
      }
      if (
        workMode === "placement" &&
        (e.key === "Delete" || e.key === "Backspace") &&
        selectedIds.length > 0
      ) {
        e.preventDefault();
        void removePinsFromMap(selectedIds);
        return;
      }
      if (
        workMode === "placement" &&
        (e.ctrlKey || e.metaKey) &&
        e.key.toLowerCase() === "a" &&
        editMode
      ) {
        e.preventDefault();
        const hall = halls.find((h) => h.id === activeHall);
        const ids = companiesRef.current
          .filter(
            (c) =>
              isPlaced(c) &&
              (c.mapPoint.hallId === hall?.id ||
                c.hall === hall?.label ||
                c.hall === hall?.name ||
                !c.mapPoint.hallId),
          )
          .map((c) => c.id);
        if (!ids.length) return;
        setSelectedIds(ids);
        setSelectedCompany(ids[ids.length - 1] ?? null);
      }
      if (e.key === "Escape") {
        setMarkerToolId(null);
        setSelectedMarkerId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, editMode, activeHall, halls, workMode, selectedMarkerId, undoDepth, redoDepth, sceneDirty]);

  const activeHallObj = halls.find((h) => h.id === activeHall);
  // API already prepares SVG with crop + board size — do not re-crop on client.
  const floorplanSvg = activeHallObj?.floorplanSvg ?? "";
  const floorplanScene = sceneDraft ?? activeHallObj?.floorplanScene;
  const hasScene = Boolean(floorplanScene && sceneHasContent(floorplanScene));
  const hasUnderlay = Boolean(floorplanSvg);
  const hasPlan = hasUnderlay || hasScene;
  /** All scheme markers including legacy exhibitor booths. */
  const schemeMarkers = useMemo(
    () => floorplanScene?.objects ?? [],
    [floorplanScene],
  );
  /** Technical objects only — офис / вход / туалет… (not exhibitor booths). */
  const techMarkers = useMemo(
    () => schemeMarkers.filter((o) => o.kind !== "booth"),
    [schemeMarkers],
  );
  const boothMarkerCount = useMemo(
    () => schemeMarkers.filter((o) => o.kind === "booth").length,
    [schemeMarkers],
  );
  const selectedMarker = useMemo(
    () => schemeMarkers.find((o) => o.id === selectedMarkerId) ?? null,
    [schemeMarkers, selectedMarkerId],
  );

  const board = useMemo(() => {
    const native = svgNativeSize(activeHallObj);
    return boardPixels(native, boardScale);
  }, [activeHallObj, boardScale]);

  const focusOnBoardPct = useCallback(
    (xPct: number, yPct: number) => {
      const { fx, fy } = boardPctToFramePx(
        xPct,
        yPct,
        board.width,
        board.height,
        boardRotation,
      );
      mapViewportRef.current?.focusPoint(fx, fy);
    },
    [board.width, board.height, boardRotation],
  );

  useEffect(() => {
    const native = svgNativeSize(activeHallObj);
    nativeBoardRef.current = native;
    // Board CSS size stays ~100% of SVG; workspace zoom is viewport (slider top-right).
    // Old slider wrote 15%/200% into floorplanWidth — that only blew up labels.
    if (activeHallObj?.floorplanWidth && activeHallObj.floorplanHeight && native.w > 0) {
      const scale = Math.round((activeHallObj.floorplanWidth / native.w) * 100);
      const normalized = scale < 70 || scale > 140 ? 100 : Math.min(200, Math.max(15, scale));
      setBoardScale(normalized);
      if (normalized === 100 && scale !== 100 && id && activeHallObj.id) {
        void persistBoardSize(100);
      }
    } else {
      setBoardScale(100);
    }
    setCropDraft({
      left: activeHallObj?.crop?.left ?? 0,
      top: activeHallObj?.crop?.top ?? 0,
      right: activeHallObj?.crop?.right ?? 0,
      bottom: activeHallObj?.crop?.bottom ?? 0,
    });
    const rot = activeHallObj?.floorplanScene?.rotationDeg ?? 0;
    setBoardRotation(rot === 90 || rot === 180 || rot === 270 ? rot : 0);
    const scene = activeHallObj?.floorplanScene;
    // Respect persisted underlayVisible (default on when SVG exists).
    setShowUnderlay(
      Boolean(activeHallObj?.floorplanSvg) && scene?.underlayVisible !== false,
    );
    setShowOverlay(true);
    // Booth markers duplicate exhibitor pins — keep them hidden by default.
    setShowSchemeBooths(false);
    // Corel stand letters are crooked noise once pins exist — keep texts off by default.
    setUnderlayLayers({
      ...resolveUnderlaySvgLayers(scene?.underlayLayers),
      texts: false,
    });
    setLabelScale(
      typeof scene?.labelScale === "number" && scene.labelScale > 0
        ? Math.min(2.2, Math.max(0.7, scene.labelScale))
        : 1.15,
    );
    setPinLabelRotationDeg(normalizePinLabelRot(scene?.pinLabelRotationDeg));
    setLabelsUpright(scene?.labelsUpright !== false);
    setSelectedMarkerId(null);
    setMarkerToolId(null);
    sceneUndoRef.current = [];
    sceneRedoRef.current = [];
    setUndoDepth(0);
    setRedoDepth(0);
    setSceneDirty(false);
    if (scene) {
      setSceneDraft(scene);
    } else if (activeHallObj?.floorplanSvg) {
      const s = parseSvgBoardSize(activeHallObj.floorplanSvg);
      setSceneDraft(
        createEmptyFloorplanScene(
          activeHallObj.floorplanWidth ?? s.width,
          activeHallObj.floorplanHeight ?? s.height,
          { format: "manual" },
        ),
      );
    } else {
      setSceneDraft(null);
    }
    // Reset draft only when switching hall / reloading underlay — not on every marker save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeHall, floorplanSvg.length]);

  /**
   * Our usable scheme (booth cells) — independent of Corel underlay toggle.
   * Prefer SVG-labeled geometry (same space as pins/autoplace); scene is fallback.
   */
  const ourSchemeScene = useMemo((): FloorplanScene | null => {
    const boardW = board.width;
    const boardH = board.height;
    const pad = floorplanScene?.contentPad;
    const labeled = floorplanSvg
      ? mapBoxesThroughContentPad(parseLabeledBoothRectsFromSvg(floorplanSvg), pad)
      : [];
    if (labeled.length) {
      const booths: FloorplanSceneObject[] = labeled.map((b, i) => ({
        id: `booth-svg-${b.code}-${i}`,
        kind: "booth",
        code: b.code,
        label: b.code,
        x: b.x,
        y: b.y,
        w: Math.max(1.2, b.w),
        h: Math.max(1.2, b.h),
      }));
      return { version: 1, width: boardW, height: boardH, objects: booths };
    }
    const fromScene = floorplanScene ? sceneBoothRects(floorplanScene) : [];
    if (fromScene.length && floorplanScene) {
      return {
        version: 1,
        width: floorplanScene.width || boardW,
        height: floorplanScene.height || boardH,
        objects: floorplanScene.objects.filter((o) => o.kind === "booth"),
      };
    }
    return null;
  }, [floorplanScene, floorplanSvg, board.width, board.height]);
  /**
   * Legacy SVG→scene ghost only when we have no editable booth markers yet.
   * Toggle «Схема стендов» always works (never forced on).
   */
  const showOurScheme = Boolean(
    ourSchemeScene && boothMarkerCount === 0 && showSchemeBooths,
  );
  const schemeAsGhost = Boolean(showOurScheme && showUnderlay);
  const visibleOverlayMarkers = useMemo(
    () =>
      schemeMarkers.filter((o) =>
        o.kind === "booth" ? showSchemeBooths : showOverlay,
      ),
    [schemeMarkers, showSchemeBooths, showOverlay],
  );

  const openLayersMenu = () => {
    const el = layersBtnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setLayersMenuPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
    setLayersOpen((v) => !v);
  };

  boardRotationRef.current = boardRotation;
  boardSizeRef.current = { w: board.width, h: board.height };
  viewZoomRef.current = viewZoom;

  const pointerOnBoard = useCallback(
    (clientX: number, clientY: number) => {
      const frame = mapFrameRef.current;
      if (!frame) return { x: 0, y: 0 };
      return boardLocalPercent(
        clientX,
        clientY,
        frame.getBoundingClientRect(),
        board.width,
        board.height,
        boardRotation,
        viewZoomRef.current,
      );
    },
    [board.width, board.height, boardRotation],
  );

  const boothTitles = useMemo(() => {
    const m: Record<string, string> = {};
    for (const c of companies) {
      const code = normalizeBoothCode(c.booth);
      if (!code) continue;
      if (m[code]) {
        if (!m[code]!.toLowerCase().includes(c.name.toLowerCase())) {
          m[code] = `${m[code]} · ${c.name}`;
        }
      } else {
        m[code] = c.name;
      }
    }
    return m;
  }, [companies]);

  const hallCompanies = useMemo(
    () =>
      companies.filter((c) =>
        activeHallObj
          ? c.hall === activeHallObj.label ||
            c.hall === activeHallObj.name ||
            c.mapPoint.hallId === activeHallObj.id ||
            !isPlaced(c)
          : true,
      ),
    [companies, activeHallObj],
  );

  const placedOnHall = useMemo(
    () =>
      hallCompanies.filter(
        (c) =>
          isPlaced(c) &&
          (c.mapPoint.hallId === activeHallObj?.id ||
            c.hall === activeHallObj?.label ||
            c.hall === activeHallObj?.name),
      ),
    [hallCompanies, activeHallObj?.id, activeHallObj?.label, activeHallObj?.name],
  );

  const adminPinGroups = useMemo((): AdminPinGroup[] => {
    const round1 = (n: number) => Math.round(n * 10) / 10;
    const byKey = new Map<string, Company[]>();
    for (const c of placedOnHall) {
      const booth = (c.booth || "").trim().toUpperCase();
      const { w, h } = pinSize(c);
      // Same booth + same geometry → group card; «Разложить» даёт разные ключи.
      const key = booth
        ? `${booth}@${round1(c.mapPoint.x)},${round1(c.mapPoint.y)},${round1(w)},${round1(h)}`
        : `solo:${c.id}`;
      const list = byKey.get(key);
      if (list) list.push(c);
      else byKey.set(key, [c]);
    }
    return [...byKey.entries()].map(([key, list]) => {
      const primary = list[0]!;
      const box = pinBox(primary);
      return {
        key,
        booth: primary.booth?.trim() || "",
        companies: [...list].sort((a, b) => a.name.localeCompare(b.name, "ru")),
        left: box.left,
        top: box.top,
        w: box.w,
        h: box.h,
        cx: primary.mapPoint.x,
        cy: primary.mapPoint.y,
      };
    });
  }, [placedOnHall]);

  const listFiltered = useMemo(() => {
    const q = listSearch.trim().toLowerCase();
    if (!q) return hallCompanies;
    return hallCompanies.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.booth || "").toLowerCase().includes(q) ||
        (c.category || "").toLowerCase().includes(q),
    );
  }, [hallCompanies, listSearch]);

  const searchFiltered = useMemo(
    () =>
      search
        ? companies.filter(
            (c) =>
              c.name.toLowerCase().includes(search.toLowerCase()) ||
              c.booth.toLowerCase().includes(search.toLowerCase()),
          )
        : [],
    [companies, search],
  );

  const selectedComp = companies.find((c) => c.id === selectedCompany);
  const selectedIndex = companies.findIndex((c) => c.id === selectedCompany);

  useEffect(() => {
    if (!selectedComp) return;
    setBoothDraft(selectedComp.booth || "");
    setMapLabelDraft((selectedComp.mapLabel ?? "").trim());
    setMapLabelLinesDraft(pinNameLines(selectedComp.mapLabelLines));
    setMapLabelScaleDraft(
      typeof selectedComp.mapLabelScale === "number" && selectedComp.mapLabelScale > 0
        ? Math.min(2.5, Math.max(0.5, selectedComp.mapLabelScale))
        : 1,
    );
    setColorDraft(pinColorFor(selectedComp, Math.max(0, selectedIndex)));
    setPlaceMode(false);
  }, [selectedComp?.id]);

  const placePin = async (
    companyId: string,
    x: number,
    y: number,
    extras?: { booth?: string; pinColor?: string; mapW?: number; mapH?: number; snap?: boolean },
  ) => {
    if (!id || !activeHallObj) return;
    const current = companies.find((c) => c.id === companyId);
    const booth = String(
      extras?.booth !== undefined && extras.booth !== null
        ? extras.booth
        : boothDraft || current?.booth || "",
    ).trim();
    const pinColor = extras?.pinColor ?? colorDraft;
    let nextX = x;
    let nextY = y;
    let nextW = extras?.mapW ?? current?.mapPoint.w ?? DEFAULT_PIN_W;
    let nextH = extras?.mapH ?? current?.mapPoint.h ?? DEFAULT_PIN_H;
    if (extras?.snap !== false) {
      // Snap by booth NUMBER first — proximity-only snap stacked A507 under A519.
      const snapped = nearestBoothAt(activeHallObj, x, y, 7, booth);
      if (snapped && extras?.mapW == null && extras?.mapH == null) {
        nextX = snapped.x;
        nextY = snapped.y;
        nextW = snapped.w;
        nextH = snapped.h;
      }
    }
    nextW = Math.min(80, Math.max(1.2, Number(nextW.toFixed(2))));
    nextH = Math.min(80, Math.max(1.2, Number(nextH.toFixed(2))));
    setCompanies((prev) =>
      prev.map((c) =>
        c.id === companyId
          ? {
              ...c,
              booth: booth.trim() || c.booth,
              hall: preferredHallCaption(activeHallObj),
              pinColor,
              mapPoint: {
                x: nextX,
                y: nextY,
                w: nextW,
                h: nextH,
                hallId: activeHallObj.id,
              },
            }
          : c,
      ),
    );
    try {
      const updated = await mapService.updateBooth(id, companyId, {
        mapX: nextX,
        mapY: nextY,
        mapW: nextW,
        mapH: nextH,
        booth: booth.trim() || undefined,
        hall: preferredHallCaption(activeHallObj),
        hallId: activeHallObj.id,
        pinColor,
      });
      setCompanies((prev) => prev.map((c) => (c.id === companyId ? { ...c, ...updated } : c)));
      setSaveError("");
      setPlaceMode(false);
    } catch {
      setSaveError("Не удалось сохранить позицию стенда");
      await reloadMap();
    }
  };

  const saveBoothMeta = async () => {
    if (!id || !selectedCompany) return;
    try {
      const updated = await mapService.updateBooth(id, selectedCompany, {
        booth: boothDraft.trim(),
        pinColor: colorDraft,
        hallId: activeHallObj?.id,
      });
      setCompanies((prev) => prev.map((c) => (c.id === selectedCompany ? { ...c, ...updated } : c)));
      setSaveError("");
    } catch {
      setSaveError("Не удалось сохранить номер стенда / цвет");
    }
  };

  const savePinLabel = async () => {
    if (!id || !selectedCompany) return;
    const trimmed = mapLabelDraft.trim();
    const mapLabel = trimmed && trimmed !== selectedComp?.name ? trimmed : null;
    try {
      const updated = await mapService.updateBooth(id, selectedCompany, {
        mapLabel,
        mapLabelLines: mapLabelLinesDraft,
      });
      setCompanies((prev) => prev.map((c) => (c.id === selectedCompany ? { ...c, ...updated } : c)));
      setSaveError("");
    } catch {
      setSaveError("Не удалось сохранить подпись на пине");
    }
  };

  const savePinLabelLines = async (lines: 1 | 2 | 3) => {
    if (!id || !selectedCompany) return;
    setMapLabelLinesDraft(lines);
    setCompanies((prev) =>
      prev.map((c) => (c.id === selectedCompany ? { ...c, mapLabelLines: lines } : c)),
    );
    try {
      const updated = await mapService.updateBooth(id, selectedCompany, {
        mapLabelLines: lines,
      });
      setCompanies((prev) => prev.map((c) => (c.id === selectedCompany ? { ...c, ...updated } : c)));
      setSaveError("");
    } catch {
      setSaveError("Не удалось сохранить число строк подписи");
    }
  };

  const savePinLabelScale = async (scale: number) => {
    if (!id || !selectedCompany) return;
    const next = Math.min(2.5, Math.max(0.5, Number(scale.toFixed(2))));
    setMapLabelScaleDraft(next);
    setCompanies((prev) =>
      prev.map((c) => (c.id === selectedCompany ? { ...c, mapLabelScale: next } : c)),
    );
    try {
      const updated = await mapService.updateBooth(id, selectedCompany, {
        mapLabelScale: next,
      });
      setCompanies((prev) => prev.map((c) => (c.id === selectedCompany ? { ...c, ...updated } : c)));
      setSaveError("");
    } catch {
      setSaveError("Не удалось сохранить размер шрифта");
    }
  };

  const setMapLocked = async (companyId: string, locked: boolean) => {
    if (!id) return;
    setCompanies((prev) =>
      prev.map((c) => (c.id === companyId ? { ...c, mapLocked: locked } : c)),
    );
    try {
      const updated = await mapService.updateBooth(id, companyId, { mapLocked: locked });
      setCompanies((prev) => prev.map((c) => (c.id === companyId ? { ...c, ...updated } : c)));
      setSaveError("");
    } catch {
      setSaveError(locked ? "Не удалось закрепить стенд" : "Не удалось открепить стенд");
      await reloadMap();
    }
  };

  const selectCompany = (companyId: string, opts?: { additive?: boolean }) => {
    setSelectedMarkerId(null);
    setMarkerToolId(null);
    setMarkerLabelDraft("");
    setMarkerCodeDraft("");
    if (opts?.additive) {
      setSelectedIds((prev) => {
        if (prev.includes(companyId)) {
          const next = prev.filter((x) => x !== companyId);
          setSelectedCompany(next[next.length - 1] ?? null);
          return next;
        }
        setSelectedCompany(companyId);
        return [...prev, companyId];
      });
      return;
    }
    setSelectedCompany(companyId);
    setSelectedIds([companyId]);
    // Do not auto-zoom/center — that forced every resize to start with a jump to viewport center.
  };

  const clearCompanySelection = () => {
    setSelectedCompany(null);
    setSelectedIds([]);
    setPlaceMode(false);
  };

  const removePinsFromMap = async (companyIds: string[]) => {
    if (!id || !companyIds.length) return;
    const unique = [...new Set(companyIds)];
    const ok = window.confirm(
      unique.length === 1
        ? "Убрать этот объект с карты? Компания останется в списке — без позиции на схеме."
        : `Убрать с карты ${unique.length} объектов? Компании останутся в списке.`,
    );
    if (!ok) return;
    setSaveError("");
    try {
      for (const companyId of unique) {
        await mapService.updateBooth(id, companyId, {
          mapX: null,
          mapY: null,
          mapW: null,
          mapH: null,
          mapLocked: false,
        });
      }
      setCompanies((prev) =>
        prev.map((c) =>
          unique.includes(c.id)
            ? {
                ...c,
                mapLocked: false,
                mapPoint: {
                  ...c.mapPoint,
                  x: 0,
                  y: 0,
                  w: undefined,
                  h: undefined,
                },
              }
            : c,
        ),
      );
      clearCompanySelection();
      setSaveError(
        unique.length === 1 ? "Объект убран с карты" : `Убрано с карты: ${unique.length}`,
      );
    } catch {
      setSaveError("Не удалось убрать объект(ы) с карты");
      await reloadMap();
    }
  };

  const PIN_DRAG_THRESHOLD_PX = 6;

  /** Only same booth + same geometry (group card). Spread pins stay independent. */
  const boothMateIds = (companyId: string): string[] => {
    const target = companiesRef.current.find((c) => c.id === companyId);
    if (!target) return [companyId];
    const booth = (target.booth || "").trim().toUpperCase();
    if (!booth) return [companyId];
    const round1 = (n: number) => Math.round(n * 10) / 10;
    const { w: tw, h: th } = pinSize(target);
    const tx = round1(target.mapPoint.x);
    const ty = round1(target.mapPoint.y);
    const mates = companiesRef.current
      .filter((c) => {
        if (!isPlaced(c)) return false;
        if ((c.booth || "").trim().toUpperCase() !== booth) return false;
        if (
          !(
            c.mapPoint.hallId === target.mapPoint.hallId ||
            c.hall === target.hall ||
            !target.mapPoint.hallId
          )
        ) {
          return false;
        }
        const { w, h } = pinSize(c);
        return (
          round1(c.mapPoint.x) === tx &&
          round1(c.mapPoint.y) === ty &&
          round1(w) === round1(tw) &&
          round1(h) === round1(th)
        );
      })
      .map((c) => c.id);
    return mates.length ? mates : [companyId];
  };

  /** Soft floor only — user may shrink group cards freely (names truncate). */
  const groupMinSize = (_mateCount: number): { minW: number; minH: number } => ({
    minW: 1.5,
    minH: 1.5,
  });

  const onPinPointerDown =
    (companyId: string, mateIds?: string[]) => (e: React.PointerEvent) => {
    if (!editMode || cropMode) return;
    // Shift+click — multi-select, no drag.
    if (e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      selectCompany(companyId, { additive: true });
      return;
    }
    const target = companiesRef.current.find((c) => c.id === companyId);
    if (target?.mapLocked) {
      e.preventDefault();
      e.stopPropagation();
      selectCompany(companyId);
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    resizeState.current = null;
    const mates = mateIds?.length ? mateIds : boothMateIds(companyId);
    if (mates.some((id) => companiesRef.current.find((c) => c.id === id)?.mapLocked)) {
      selectCompany(companyId);
      setSaveError("В группе есть закреплённые — сначала открепите");
      return;
    }
    pinDrag.current = {
      companyId,
      mateIds: mates,
      startClientX: e.clientX,
      startClientY: e.clientY,
      originX: target?.mapPoint.x ?? 0,
      originY: target?.mapPoint.y ?? 0,
      moved: false,
    };
    selectCompany(companyId);
  };

  type ResizeHandle = "se" | "sw" | "ne" | "nw" | "n" | "s" | "e" | "w";

  const onResizePointerDown =
    (companyId: string, corner: ResizeHandle, mateIds?: string[], boxOverride?: { left: number; top: number; w: number; h: number }) =>
    (e: React.PointerEvent) => {
      if (!editMode || cropMode) return;
      e.preventDefault();
      e.stopPropagation();
      const c = companies.find((x) => x.id === companyId);
      if (!c || c.mapLocked) return;
      const mates = mateIds?.length ? mateIds : boothMateIds(companyId);
      if (mates.some((id) => companies.find((x) => x.id === id)?.mapLocked)) {
        setSaveError("В группе есть закреплённые — сначала открепите");
        return;
      }
      const box = boxOverride ?? pinBox(c);
      pinDrag.current = null;
      resizeState.current = {
        companyId,
        mateIds: mates,
        corner,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startLeft: box.left,
        startTop: box.top,
        startW: box.w,
        startH: box.h,
        moved: false,
      };
      selectCompany(companyId);
    };

  // Window-level pin drag/resize — handles use setPointerCapture otherwise and lose move events.
  const placePinRef = useRef(placePin);
  placePinRef.current = placePin;
  const editModeRef = useRef(editMode);
  editModeRef.current = editMode;
  const cropModeRef = useRef(cropMode);
  cropModeRef.current = cropMode;

  useEffect(() => {
    const onCropMove = (e: PointerEvent) => {
      const drag = cropDrag.current;
      if (!drag || !cropModeRef.current) return;
      const { dx, dy } = screenDeltaToBoardPct(
        e.clientX - drag.startClientX,
        e.clientY - drag.startClientY,
        boardSizeRef.current.w,
        boardSizeRef.current.h,
        boardRotationRef.current,
        viewZoomRef.current,
      );
      let next = { ...drag.startTrim };
      const edge = drag.edge;
      if (edge.includes("w")) next.left = Math.max(0, Math.min(40, drag.startTrim.left + dx));
      if (edge.includes("e")) next.right = Math.max(0, Math.min(40, drag.startTrim.right - dx));
      if (edge.includes("n")) next.top = Math.max(0, Math.min(40, drag.startTrim.top + dy));
      if (edge.includes("s")) next.bottom = Math.max(0, Math.min(40, drag.startTrim.bottom - dy));
      if (next.left + next.right > 70) {
        const s = 70 / (next.left + next.right);
        next.left *= s;
        next.right *= s;
      }
      if (next.top + next.bottom > 70) {
        const s = 70 / (next.top + next.bottom);
        next.top *= s;
        next.bottom *= s;
      }
      setCropTrim({
        left: Number(next.left.toFixed(1)),
        right: Number(next.right.toFixed(1)),
        top: Number(next.top.toFixed(1)),
        bottom: Number(next.bottom.toFixed(1)),
      });
    };
    const onCropUp = () => {
      if (!cropDrag.current) return;
      cropDrag.current = null;
    };
    window.addEventListener("pointermove", onCropMove);
    window.addEventListener("pointerup", onCropUp);
    window.addEventListener("pointercancel", onCropUp);
    return () => {
      window.removeEventListener("pointermove", onCropMove);
      window.removeEventListener("pointerup", onCropUp);
      window.removeEventListener("pointercancel", onCropUp);
    };
  }, []);

  const onCropHandleDown =
    (edge: "n" | "s" | "e" | "w" | "nw" | "ne" | "sw" | "se") =>
    (e: React.PointerEvent) => {
      if (!cropMode) return;
      e.preventDefault();
      e.stopPropagation();
      cropDrag.current = {
        edge,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startTrim: { ...cropTrimRef.current },
      };
    };

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!editModeRef.current) return;
      if (resizeState.current) {
        const rs = resizeState.current;
        const dist = Math.hypot(e.clientX - rs.startClientX, e.clientY - rs.startClientY);
        if (!rs.moved && dist < PIN_DRAG_THRESHOLD_PX) return;
        rs.moved = true;
        const { dx, dy } = screenDeltaToBoardPct(
          e.clientX - rs.startClientX,
          e.clientY - rs.startClientY,
          boardSizeRef.current.w,
          boardSizeRef.current.h,
          boardRotationRef.current,
          viewZoomRef.current,
        );
        let left = rs.startLeft;
        let top = rs.startTop;
        let w = rs.startW;
        let h = rs.startH;
        if (rs.corner.includes("e")) w = rs.startW + dx;
        if (rs.corner.includes("s")) h = rs.startH + dy;
        if (rs.corner.includes("w")) {
          w = rs.startW - dx;
          left = rs.startLeft + dx;
        }
        if (rs.corner.includes("n")) {
          h = rs.startH - dy;
          top = rs.startTop + dy;
        }
        const mateCount = rs.mateIds.length || 1;
        const { minW, minH } = groupMinSize(mateCount);
        w = Math.min(80, Math.max(minW, w));
        h = Math.min(80, Math.max(minH, h));
        // Keep the anchored corner fixed when clamping min size.
        if (rs.corner.includes("w")) left = rs.startLeft + rs.startW - w;
        if (rs.corner.includes("n")) top = rs.startTop + rs.startH - h;
        left = Math.max(0, Math.min(100 - w, left));
        top = Math.max(0, Math.min(100 - h, top));
        const cx = Number((left + w / 2).toFixed(2));
        const cy = Number((top + h / 2).toFixed(2));
        const mateSet = new Set(rs.mateIds.length ? rs.mateIds : [rs.companyId]);
        setCompanies((prev) =>
          prev.map((c) =>
            mateSet.has(c.id)
              ? {
                  ...c,
                  mapPoint: {
                    ...c.mapPoint,
                    x: cx,
                    y: cy,
                    w: Number(w.toFixed(2)),
                    h: Number(h.toFixed(2)),
                  },
                }
              : c,
          ),
        );
        return;
      }
      const drag = pinDrag.current;
      if (!drag) return;
      const dist = Math.hypot(e.clientX - drag.startClientX, e.clientY - drag.startClientY);
      if (!drag.moved && dist < PIN_DRAG_THRESHOLD_PX) return;
      drag.moved = true;
      const { dx, dy } = screenDeltaToBoardPct(
        e.clientX - drag.startClientX,
        e.clientY - drag.startClientY,
        boardSizeRef.current.w,
        boardSizeRef.current.h,
        boardRotationRef.current,
        viewZoomRef.current,
      );
      const nextX = Number(Math.max(0, Math.min(100, drag.originX + dx)).toFixed(2));
      const nextY = Number(Math.max(0, Math.min(100, drag.originY + dy)).toFixed(2));
      const mateSet = new Set(drag.mateIds.length ? drag.mateIds : [drag.companyId]);
      setCompanies((prev) =>
        prev.map((c) =>
          mateSet.has(c.id) ? { ...c, mapPoint: { ...c.mapPoint, x: nextX, y: nextY } } : c,
        ),
      );
    };

    const onUp = () => {
      if (!editModeRef.current) {
        pinDrag.current = null;
        resizeState.current = null;
        return;
      }
      if (resizeState.current) {
        const rs = resizeState.current;
        resizeState.current = null;
        if (!rs.moved) return;
        const mates = rs.mateIds.length ? rs.mateIds : [rs.companyId];
        void (async () => {
          for (const mid of mates) {
            const cur = companiesRef.current.find((x) => x.id === mid);
            if (!cur) continue;
            await placePinRef.current(mid, cur.mapPoint.x, cur.mapPoint.y, {
              mapW: cur.mapPoint.w,
              mapH: cur.mapPoint.h,
              snap: false,
            });
          }
        })();
        return;
      }
      const drag = pinDrag.current;
      pinDrag.current = null;
      if (!drag?.moved) return;
      const mates = drag.mateIds.length ? drag.mateIds : [drag.companyId];
      const primary = companiesRef.current.find((x) => x.id === drag.companyId);
      if (!primary) return;
      void (async () => {
        for (const mid of mates) {
          const cur = companiesRef.current.find((x) => x.id === mid) ?? primary;
          await placePinRef.current(mid, primary.mapPoint.x, primary.mapPoint.y, {
            mapW: primary.mapPoint.w ?? cur.mapPoint.w,
            mapH: primary.mapPoint.h ?? cur.mapPoint.h,
            snap: false,
          });
        }
      })();
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, []);

  /** Split companies that share a booth number into separate stacked pins inside the booth box. */
  const spreadBoothMates = async () => {
    if (!id || !selectedComp || !isPlaced(selectedComp)) return;
    const booth = (selectedComp.booth || "").trim().toUpperCase();
    if (!booth) {
      setSaveError("У компании нет номера стенда — разложить нечего");
      return;
    }
    const mates = companiesRef.current.filter(
      (c) => (c.booth || "").trim().toUpperCase() === booth && isPlaced(c),
    );
    if (mates.length < 2) {
      setSaveError("На этом номере только одна компания на карте");
      return;
    }
    if (mates.some((c) => c.mapLocked)) {
      setSaveError("Сначала открепите всех участников стенда");
      return;
    }
    const box = pinBox(selectedComp);
    const n = mates.length;
    const sliceH = box.h / n;
    const sorted = [...mates].sort((a, b) => a.name.localeCompare(b.name, "ru"));
    try {
      for (let i = 0; i < sorted.length; i++) {
        const c = sorted[i]!;
        const cy = Number((box.top + sliceH * (i + 0.5)).toFixed(2));
        const cx = Number((box.left + box.w / 2).toFixed(2));
        const h = Number(Math.max(1.2, sliceH * 0.92).toFixed(2));
        const w = Number(box.w.toFixed(2));
        await placePin(c.id, cx, cy, { mapW: w, mapH: h, snap: false });
      }
      setSaveError("");
    } catch {
      setSaveError("Не удалось разложить компании стенда");
      await reloadMap();
    }
  };

  /** Merge mates that share a booth into one group card (same geometry). */
  const gatherBoothMates = async () => {
    if (!id || !selectedComp || !isPlaced(selectedComp)) return;
    const booth = (selectedComp.booth || "").trim().toUpperCase();
    if (!booth) {
      setSaveError("У компании нет номера стенда");
      return;
    }
    const mates = companiesRef.current.filter(
      (c) => (c.booth || "").trim().toUpperCase() === booth && isPlaced(c),
    );
    if (mates.length < 2) {
      setSaveError("На этом номере только одна компания на карте");
      return;
    }
    if (mates.some((c) => c.mapLocked)) {
      setSaveError("Сначала открепите всех участников стенда");
      return;
    }
    const box = pinBox(selectedComp);
    const { minW, minH } = groupMinSize(mates.length);
    const w = Number(Math.max(minW, box.w).toFixed(2));
    const h = Number(Math.max(minH, box.h).toFixed(2));
    const cx = Number((box.left + box.w / 2).toFixed(2));
    const cy = Number((box.top + box.h / 2).toFixed(2));
    try {
      for (const c of mates) {
        await placePin(c.id, cx, cy, {
          mapW: w,
          mapH: h,
          snap: false,
        });
      }
      setSaveError(`Группа «${booth}»: ${mates.length} компаний в одной карточке`);
    } catch {
      setSaveError("Не удалось собрать групповую карточку");
      await reloadMap();
    }
  };

  const snapSelectedToBooth = async () => {
    if (!selectedComp || !activeHallObj || !hasPlan) return;
    if (selectedComp.mapLocked) {
      setSaveError("Стенд закреплён — сначала открепите");
      return;
    }
    const boothCode = selectedComp.booth?.trim() || "";
    const snapped = nearestBoothAt(
      activeHallObj,
      selectedComp.mapPoint.x || 50,
      selectedComp.mapPoint.y || 50,
      100,
      boothCode,
    );
    if (!snapped) {
      setSaveError(
        boothCode
          ? `На схеме нет ячейки стенда ${boothCode} — пин не привязан к чужому стенду`
          : "На схеме не найден полигон стенда рядом с пином",
      );
      return;
    }
    await placePin(selectedComp.id, snapped.x, snapped.y, {
      booth: boothCode || undefined,
      mapW: snapped.w,
      mapH: snapped.h,
      snap: false,
    });
  };

  const handleFloorplanUpload = async (file: File | null) => {
    if (!id || !file || !activeHallObj) return;
    const lower = file.name.toLowerCase();
    const okSvg = lower.endsWith(".svg") || file.type === "image/svg+xml";
    const okCdr = isCdrFileName(file.name);
    const okRaster =
      lower.endsWith(".png") ||
      lower.endsWith(".jpg") ||
      lower.endsWith(".jpeg") ||
      lower.endsWith(".webp") ||
      file.type === "image/png" ||
      file.type === "image/jpeg" ||
      file.type === "image/webp";
    if (!okSvg && !okCdr && !okRaster) {
      setSaveError("Нужен файл: .png / .jpg / .svg / .cdr");
      return;
    }
    setUploadingSvg(true);
    setSaveError("");
    try {
      if (okRaster && !okSvg && !okCdr) {
        const svg = await rasterFileToSvgUnderlay(file);
        const hall = await mapService.uploadFloorplan(id, activeHallObj.id, svg);
        applyHallUpdate(hall);
        setBoardScale(100);
        setWorkMode("scheme");
        setShowUnderlay(true);
        setShowOverlay(true);
        setShowSchemeBooths(true);
        setSaveError(
          "PNG/JPG подложка готова (это картинка). Номера A### на ней система не читает — авторасстановка только из .svg/.cdr. Пины ставьте вручную или «Подогнать по стенду».",
        );
        return;
      }
      if (okSvg && !okCdr) {
        const text = await file.text();
        if (!text.includes("<svg")) {
          setSaveError("Файл не похож на SVG");
          return;
        }
        const hall = await mapService.uploadFloorplan(id, activeHallObj.id, text);
        applyHallUpdate(hall);
        setBoardScale(100);
        setWorkMode("scheme");
        setShowUnderlay(true);
        setShowOverlay(true);
        const labeledCount = parseLabeledBoothRectsFromSvg(text).length;
        const outlined = /id=["']glyph-|xlink:href=["']#glyph-/i.test(text) && !/<text[\s>]/i.test(text);
        if (labeledCount > 0) {
          setSaveError(
            `Подложка загружена. Найдено номеров стендов: ${labeledCount}. Можно «Авторасстановка».`,
          );
        } else if (outlined) {
          setSaveError(
            "SVG загружен, но номера — кривые (glyph), не текст. Авторасстановка не сработает: нужен .cdr или SVG с живым текстом A###.",
          );
        } else {
          setSaveError(
            "Подложка загружена, но текстовых номеров A### не найдено. Для авторасстановки нужен .cdr / SVG с живым текстом.",
          );
        }
        return;
      }

      const { hall, source, boothCodes } = await mapService.uploadFloorplanFile(
        id,
        activeHallObj.id,
        file,
      );
      applyHallUpdate(hall);
      setBoardScale(100);
      setWorkMode("scheme");
      setShowUnderlay(true);
      setShowOverlay(true);
      // Hide crooked Corel text by default — draw our markers on top.
      const layers = {
        ...resolveUnderlaySvgLayers(DEFAULT_UNDERLAY_SVG_LAYERS),
        texts: false,
        fills: false,
        contours: false,
        grid: true,
        decor: true,
      };
      setUnderlayLayers(layers);
      const base = ensureSceneDraft();
      if (base) {
        commitSceneLocal({ ...base, underlayLayers: layers });
        await persistSceneNow({ ...base, underlayLayers: layers });
      }
      const codesHint =
        source === "cdr" && boothCodes.length
          ? ` В файле номера: ${boothCodes.slice(0, 8).join(", ")}${boothCodes.length > 8 ? "…" : ""}.`
          : "";
      setSaveError(
        `Подложка готова, надписи Corel скрыты.${codesHint} Рисуйте маркеры поверх.`,
      );
    } catch (err) {
      const body =
        err instanceof ApiError && err.body && typeof err.body === "object"
          ? (err.body as { message?: string; hint?: string; boothCodes?: string[] })
          : null;
      const msg =
        body?.message ||
        formatApiError(err, "Не удалось загрузить схему (.png / .svg / .cdr)");
      const hint = body?.hint ? ` ${body.hint}` : "";
      const codes =
        body?.boothCodes?.length
          ? ` В файле найдены стенды: ${body.boothCodes.slice(0, 12).join(", ")}${body.boothCodes.length > 12 ? "…" : ""}.`
          : "";
      setSaveError(`${msg}${hint}${codes}`);
    } finally {
      setUploadingSvg(false);
      if (svgInputRef.current) svgInputRef.current.value = "";
    }
  };

  const autoPlaceByBoothNumbers = async () => {
    if (!id || !activeHallObj) return;
    // Prefer numbers from underlay SVG (Corel 1:1). Manual booth markers are optional.
    // SVG coords are underlay-local — remap through contentPad after «Поле ↑».
    const pad =
      sceneDraftRef.current?.contentPad ?? activeHallObj.floorplanScene?.contentPad;
    const fromScene =
      sceneDraft && sceneHasContent(sceneDraft) ? sceneBoothRects(sceneDraft) : [];
    const fromSvg = activeHallObj.floorplanSvg
      ? mapBoxesThroughContentPad(
          parseLabeledBoothRectsFromSvg(activeHallObj.floorplanSvg),
          pad,
        )
      : [];
    const labeled = fromSvg.length ? fromSvg : fromScene;
    if (!labeled.length) {
      const svg = activeHallObj.floorplanSvg ?? "";
      const hasText = /<text[\s>]/i.test(svg);
      const hasGlyphs = /id=["']glyph-|xlink:href=["']#glyph-/i.test(svg);
      const hasImage = /<image[\s>]/i.test(svg);
      if (hasGlyphs && !hasText) {
        setSaveError(
          "SVG загружен, но номера стендов в нём — кривые (не текст). Так бывает при экспорте PDF→SVG. Нужен .cdr или SVG с живым текстом A###, либо ставьте пины вручную.",
        );
      } else if (hasImage && !hasText) {
        setSaveError(
          "Подложка — картинка внутри SVG: номера видны глазу, но не читаются. Загрузите .cdr / SVG с текстовыми номерами, либо ставьте пины вручную.",
        );
      } else {
        setSaveError(
          "На подложке не найдены текстовые номера стендов (A###). Нужен .cdr или SVG с подписями `<text>`, не кривые.",
        );
      }
      return;
    }
    const plan = planAutoBoothPlacements(
      hallCompanies.map((c) => ({
        id: c.id,
        booth: c.booth,
        mapLocked: c.mapLocked,
        placed: isPlaced(c),
      })),
      labeled,
      { overwritePlaced: true },
    );
    if (!plan.placements.length) {
      setSaveError(
        plan.unmatchedCompanies.length
          ? `Нет совпадений номеров стендов (на схеме ${labeled.length}, у компаний без совпадения: ${plan.unmatchedCompanies.length})`
          : "Нечего расставлять — у компаний нет номеров стендов или все закреплены",
      );
      return;
    }
    const ok = window.confirm(
      `Расставить ${plan.placements.length} компаний по номерам стендов?\n` +
        (plan.unmatchedCompanies.length
          ? `Без совпадения: ${plan.unmatchedCompanies.length}. `
          : "") +
        (plan.skippedLocked ? `Пропущено закреплённых: ${plan.skippedLocked}.` : ""),
    );
    if (!ok) return;
    setSaveError("");
    try {
      for (const p of plan.placements) {
        await placePin(p.companyId, p.x, p.y, {
          booth: p.booth,
          mapW: p.w,
          mapH: p.h,
          snap: false,
        });
      }
      setShowSchemeBooths(false);
      const base = sceneDraftRef.current;
      if (base) {
        const layers = {
          ...resolveUnderlaySvgLayers(underlayLayers),
          texts: false,
          fills: false,
        };
        setUnderlayLayers(layers);
        commitSceneLocal({ ...base, underlayLayers: layers, underlayVisible: true }, {
          recordUndo: false,
        });
      }
      const rest = plan.unmatchedCompanies.length
        ? ` Без места на схеме: ${plan.unmatchedCompanies.length}.`
        : "";
      const tip =
        boothMarkerCount > 0
          ? ` Маркеры-стенды скрыты — кнопка «Убрать маркеры-стенды» очистит дубли.`
          : "";
      setSaveError(`Расставлено: ${plan.placements.length}.${rest}${tip}`);
    } catch {
      setSaveError("Авторасстановка прервалась");
      await reloadMap();
    }
  };

  const persistBoardSize = async (scalePct: number) => {
    if (!id || !activeHallObj || !hasPlan) return;
    const native = nativeBoardRef.current;
    const size = boardPixels(native, scalePct);
    try {
      const hall = await mapService.updateFloorplanBoard(id, activeHallObj.id, size.width, size.height);
      setHalls((prev) => prev.map((h) => (h.id === hall.id ? { ...h, ...hall } : h)));
      setBoardScale(scalePct);
      setSaveError("");
    } catch {
      setSaveError("Не удалось сохранить размер подложки");
    }
  };

  const persistCrop = async (next = cropDraft, fromCrop?: CropInsets) => {
    if (!id || !activeHallObj) return;
    const oldCrop = fromCrop ?? {
      left: activeHallObj.crop?.left ?? 0,
      top: activeHallObj.crop?.top ?? 0,
      right: activeHallObj.crop?.right ?? 0,
      bottom: activeHallObj.crop?.bottom ?? 0,
    };
    const nextCrop = clampCropInsets(next);
    try {
      // Remap pins + scene so objects stay on the same underlay content after viewBox crop.
      const hallCompaniesIds = new Set(
        companiesRef.current
          .filter(
            (c) =>
              isPlaced(c) &&
              (c.mapPoint.hallId === activeHallObj.id ||
                c.hall === activeHallObj.label ||
                c.hall === activeHallObj.name),
          )
          .map((c) => c.id),
      );
      const remapped = companiesRef.current.map((c) => {
        if (!hallCompaniesIds.has(c.id)) return c;
        const { w, h } = pinSize(c);
        const left = c.mapPoint.x - w / 2;
        const top = c.mapPoint.y - h / 2;
        const right = left + w;
        const bottom = top + h;
        const nl = remapPctThroughCrop(left, oldCrop.left, oldCrop.right, nextCrop.left, nextCrop.right);
        const nr = remapPctThroughCrop(right, oldCrop.left, oldCrop.right, nextCrop.left, nextCrop.right);
        const nt = remapPctThroughCrop(top, oldCrop.top, oldCrop.bottom, nextCrop.top, nextCrop.bottom);
        const nb = remapPctThroughCrop(bottom, oldCrop.top, oldCrop.bottom, nextCrop.top, nextCrop.bottom);
        const nw = Math.max(1.2, Math.min(80, nr - nl));
        const nh = Math.max(1.2, Math.min(80, nb - nt));
        return {
          ...c,
          mapPoint: {
            ...c.mapPoint,
            x: Number((nl + nw / 2).toFixed(2)),
            y: Number((nt + nh / 2).toFixed(2)),
            w: Number(nw.toFixed(2)),
            h: Number(nh.toFixed(2)),
          },
        };
      });
      setCompanies(remapped);

      const baseScene = sceneDraftRef.current ?? activeHallObj.floorplanScene;
      let nextScene = baseScene;
      if (baseScene?.objects?.length) {
        nextScene = {
          ...baseScene,
          objects: baseScene.objects.map((o) => {
            const nl = remapPctThroughCrop(o.x, oldCrop.left, oldCrop.right, nextCrop.left, nextCrop.right);
            const nr = remapPctThroughCrop(
              o.x + o.w,
              oldCrop.left,
              oldCrop.right,
              nextCrop.left,
              nextCrop.right,
            );
            const nt = remapPctThroughCrop(o.y, oldCrop.top, oldCrop.bottom, nextCrop.top, nextCrop.bottom);
            const nb = remapPctThroughCrop(
              o.y + o.h,
              oldCrop.top,
              oldCrop.bottom,
              nextCrop.top,
              nextCrop.bottom,
            );
            return {
              ...o,
              x: Number(Math.max(0, Math.min(100, nl)).toFixed(2)),
              y: Number(Math.max(0, Math.min(100, nt)).toFixed(2)),
              w: Number(Math.max(1.2, Math.min(100, nr - nl)).toFixed(2)),
              h: Number(Math.max(1.2, Math.min(100, nb - nt)).toFixed(2)),
            };
          }),
        };
        setSceneDraft(nextScene);
      }

      let hall = await mapService.updateFloorplanCrop(id, activeHallObj.id, nextCrop);
      // Crop changes viewBox aspect — sync board W×H so SVG is not stretched.
      if (hall.floorplanSvg) {
        const vb = parseSvgBoardSize(hall.floorplanSvg);
        const curW = hall.floorplanWidth ?? vb.width;
        const aspect = vb.width / Math.max(1, vb.height);
        const curAspect = curW / Math.max(1, hall.floorplanHeight ?? vb.height);
        if (Math.abs(curAspect - aspect) / aspect > 0.008) {
          const nextSize =
            aspect >= 1
              ? { width: curW, height: Math.round(curW / aspect) }
              : {
                  width: Math.round((hall.floorplanHeight ?? vb.height) * aspect),
                  height: hall.floorplanHeight ?? vb.height,
                };
          hall = await mapService.updateFloorplanBoard(
            id,
            activeHallObj.id,
            nextSize.width,
            nextSize.height,
          );
          nativeBoardRef.current = { w: nextSize.width, h: nextSize.height };
          setBoardScale(100);
        }
      }
      setHalls((prev) => prev.map((h) => (h.id === hall.id ? { ...h, ...hall } : h)));
      setCropDraft(nextCrop);
      setCropTrim({ left: 0, top: 0, right: 0, bottom: 0 });

      for (const c of remapped) {
        if (!hallCompaniesIds.has(c.id)) continue;
        await mapService.updateBooth(id, c.id, {
          mapX: c.mapPoint.x,
          mapY: c.mapPoint.y,
          mapW: c.mapPoint.w,
          mapH: c.mapPoint.h,
          hallId: activeHallObj.id,
        });
      }
      if (nextScene && nextScene !== baseScene) {
        await mapService.updateFloorplanScene(id, activeHallObj.id, nextScene);
      }
      setSaveError("Обрезка применена — объекты на местах");
    } catch {
      setSaveError("Не удалось сохранить обрезку краёв");
      await reloadMap();
    }
  };

  const applyCropTrim = async () => {
    const next = absoluteCropFromTrim(cropDraftRef.current, cropTrimRef.current);
    await persistCrop(next, cropDraftRef.current);
  };

  /**
   * Grow the board on one screen edge (+empty margin for «Переход»).
   * Content pixel box keeps the same size/aspect — underlay is not squeezed
   * (old contentPad squeeze warped SVG via preserveAspectRatio=none).
   */
  const expandBoardEdge = async (
    screenEdge: "n" | "s" | "e" | "w",
    pct = 10,
  ) => {
    if (!id || !activeHallObj) return;
    const add = Math.max(2, Math.min(25, pct));
    const base = ensureSceneDraft();
    if (!base) return;
    const edge = screenEdgeToBoardEdge(screenEdge, boardRotation);

    const pad = {
      left: base.contentPad?.left ?? 0,
      top: base.contentPad?.top ?? 0,
      right: base.contentPad?.right ?? 0,
      bottom: base.contentPad?.bottom ?? 0,
    };
    const curW = board.width;
    const curH = board.height;
    const factor = add / 100;
    const growX = edge === "w" || edge === "e";
    const newW = growX
      ? Math.max(200, Math.min(5000, Math.round(curW * (1 + factor))))
      : curW;
    const newH = growX
      ? curH
      : Math.max(200, Math.min(5000, Math.round(curH * (1 + factor))));
    if (newW === curW && newH === curH) {
      setSaveError("Достигнут лимит размера поля");
      return;
    }
    const sx = curW / newW;
    const sy = curH / newH;
    // Shift content away from the grown edge (empty strip on that side).
    const ox = edge === "e" ? 0 : edge === "w" ? (1 - sx) * 100 : 0;
    const oy = edge === "s" ? 0 : edge === "n" ? (1 - sy) * 100 : 0;
    const mapX = (v: number) => Number((ox + v * sx).toFixed(2));
    const mapY = (v: number) => Number((oy + v * sy).toFixed(2));
    const nextPad = {
      left: Number((ox + pad.left * sx).toFixed(2)),
      top: Number((oy + pad.top * sy).toFixed(2)),
      right: Number(
        (edge === "e" ? (1 - sx) * 100 + pad.right * sx : pad.right * sx).toFixed(2),
      ),
      bottom: Number(
        (edge === "s" ? (1 - sy) * 100 + pad.bottom * sy : pad.bottom * sy).toFixed(2),
      ),
    };

    const nextScene: FloorplanScene = {
      ...base,
      width: newW,
      height: newH,
      contentPad: nextPad,
      objects: base.objects.map((o) => ({
        ...o,
        x: mapX(o.x),
        y: mapY(o.y),
        w: Number(Math.max(0.8, o.w * sx).toFixed(2)),
        h: Number(Math.max(0.8, o.h * sy).toFixed(2)),
      })),
    };

    const hallId = activeHallObj.id;
    const hallLabel = activeHallObj.label || activeHallObj.name || "";
    const hallCompanyIds = new Set(
      companies
        .filter(
          (c) =>
            c.mapPoint &&
            (c.mapPoint.x > 0 || c.mapPoint.y > 0) &&
            (c.mapPoint.hallId === hallId ||
              (!c.mapPoint.hallId &&
                (c.hall === hallLabel || c.hall === activeHallObj.name))),
        )
        .map((c) => c.id),
    );
    const remapped = companies
      .filter((c) => hallCompanyIds.has(c.id) && c.mapPoint)
      .map((c) => {
        const p = c.mapPoint!;
        return {
          ...c,
          mapPoint: {
            ...p,
            hallId,
            x: mapX(p.x),
            y: mapY(p.y),
            w: Number(Math.max(1.2, (p.w ?? 4) * sx).toFixed(2)),
            h: Number(Math.max(1.2, (p.h ?? 4) * sy).toFixed(2)),
          },
        };
      });

    setUploadingSvg(true);
    try {
      const hallSized = await mapService.updateFloorplanBoard(id, hallId, newW, newH);
      setHalls((prev) => prev.map((h) => (h.id === hallSized.id ? { ...h, ...hallSized } : h)));
      nativeBoardRef.current = { w: newW, h: newH };
      setBoardScale(100);
      commitSceneLocal(nextScene);
      await persistSceneNow(nextScene);
      for (const c of remapped) {
        await mapService.updateBooth(id, c.id, {
          mapX: c.mapPoint.x,
          mapY: c.mapPoint.y,
          mapW: c.mapPoint.w,
          mapH: c.mapPoint.h,
          hallId,
        });
      }
      setCompanies((prev) =>
        prev.map((c) => remapped.find((r) => r.id === c.id) ?? c),
      );
      const edgeLabel =
        screenEdge === "n"
          ? "сверху"
          : screenEdge === "s"
            ? "снизу"
            : screenEdge === "w"
              ? "слева"
              : "справа";
      setSaveError(`Поле расширено ${edgeLabel} (+${add}%) без искажения SVG`);
    } catch (err) {
      setSaveError(formatApiError(err, "Не удалось расширить поле"));
      await reloadMap();
    } finally {
      setUploadingSvg(false);
    }
  };

  const applyHallUpdate = (hall: Hall) => {
    setHalls((prev) => {
      const exists = prev.some((h) => h.id === hall.id);
      if (exists) return prev.map((h) => (h.id === hall.id ? { ...h, ...hall } : h));
      return [...prev, hall];
    });
    setActiveHall(hall.id);
    if (hall.floorplanScene) setSceneDraft(hall.floorplanScene);
  };

  const ensureSceneDraft = useCallback((): FloorplanScene | null => {
    if (sceneDraftRef.current) return sceneDraftRef.current;
    if (!activeHallObj) return null;
    const native = svgNativeSize(activeHallObj);
    const empty = createEmptyFloorplanScene(
      activeHallObj.floorplanWidth ?? native.w,
      activeHallObj.floorplanHeight ?? native.h,
      { format: "manual" },
    );
    setSceneDraft(empty);
    return empty;
  }, [activeHallObj]);

  const persistSceneNow = useCallback(
    async (scene: FloorplanScene) => {
      if (!id || !activeHallObj) return;
      try {
        const hall = await mapService.updateFloorplanScene(id, activeHallObj.id, scene);
        applyHallUpdate(hall);
        setSceneDirty(false);
        setSaveError("Схема сохранена");
      } catch (err) {
        setSaveError(formatApiError(err, "Не удалось сохранить схему"));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, activeHallObj],
  );

  const pushSceneUndo = useCallback((prev: FloorplanScene) => {
    sceneUndoRef.current = [
      ...sceneUndoRef.current.slice(-39),
      cloneFloorplanScene(prev),
    ];
    sceneRedoRef.current = [];
    setUndoDepth(sceneUndoRef.current.length);
    setRedoDepth(0);
  }, []);

  /** Local edit — records undo, marks dirty; does not auto-save. */
  const commitSceneLocal = useCallback(
    (next: FloorplanScene, opts?: { recordUndo?: boolean }) => {
      if (opts?.recordUndo !== false && sceneDraftRef.current) {
        pushSceneUndo(sceneDraftRef.current);
      }
      setSceneDraft(next);
      setSceneDirty(true);
      if (persistSceneTimer.current) {
        clearTimeout(persistSceneTimer.current);
        persistSceneTimer.current = null;
      }
    },
    [pushSceneUndo],
  );

  const snapSelectedMarkerToBooth = () => {
    const base = sceneDraftRef.current;
    if (!base || !selectedMarkerId || !activeHallObj) return;
    const obj = base.objects.find((o) => o.id === selectedMarkerId);
    if (!obj) return;
    if (obj.locked) {
      setSaveError("Объект закреплён — сначала открепите");
      return;
    }
    const snapped = nearestBoothAt(
      activeHallObj,
      obj.x + obj.w / 2,
      obj.y + obj.h / 2,
      100,
      obj.code,
    );
    if (!snapped) {
      setSaveError(
        obj.code
          ? `На подложке нет ячейки ${obj.code} — объект не привязан к чужому стенду`
          : "На подложке не найден полигон стенда рядом с объектом",
      );
      return;
    }
    const next: FloorplanScene = {
      ...base,
      objects: base.objects.map((o) =>
        o.id === selectedMarkerId
          ? {
              ...o,
              x: Number(Math.max(0, snapped.x - snapped.w / 2).toFixed(2)),
              y: Number(Math.max(0, snapped.y - snapped.h / 2).toFixed(2)),
              w: snapped.w,
              h: snapped.h,
            }
          : o,
      ),
    };
    commitSceneLocal(next);
    setSaveError("");
  };

  const toggleSelectedMarkerLock = () => {
    const base = sceneDraftRef.current;
    if (!base || !selectedMarkerId) return;
    const obj = base.objects.find((o) => o.id === selectedMarkerId);
    if (!obj) return;
    const next: FloorplanScene = {
      ...base,
      objects: base.objects.map((o) =>
        o.id === selectedMarkerId ? { ...o, locked: !o.locked } : o,
      ),
    };
    commitSceneLocal(next);
  };

  const saveSceneExplicit = useCallback(async () => {
    const scene = sceneDraftRef.current;
    if (!scene) return;
    await persistSceneNow(scene);
  }, [persistSceneNow]);

  const undoScene = useCallback(() => {
    const prev = sceneUndoRef.current.pop();
    if (!prev) return;
    const cur = sceneDraftRef.current;
    if (cur) {
      sceneRedoRef.current = [...sceneRedoRef.current, cloneFloorplanScene(cur)];
    }
    setSceneDraft(prev);
    setSceneDirty(true);
    setUndoDepth(sceneUndoRef.current.length);
    setRedoDepth(sceneRedoRef.current.length);
    setSelectedMarkerId(null);
    setMarkerLabelDraft("");
    setMarkerCodeDraft("");
  }, []);

  const redoScene = useCallback(() => {
    const next = sceneRedoRef.current.pop();
    if (!next) return;
    const cur = sceneDraftRef.current;
    if (cur) {
      sceneUndoRef.current = [...sceneUndoRef.current, cloneFloorplanScene(cur)];
    }
    setSceneDraft(next);
    setSceneDirty(true);
    setUndoDepth(sceneUndoRef.current.length);
    setRedoDepth(sceneRedoRef.current.length);
    setSelectedMarkerId(null);
    setMarkerLabelDraft("");
    setMarkerCodeDraft("");
  }, []);

  const patchSceneMeta = useCallback(
    (
      patch: Partial<
        Pick<
          FloorplanScene,
          | "underlayVisible"
          | "labelScale"
          | "pinLabelRotationDeg"
          | "labelsUpright"
          | "rotationDeg"
          | "underlayLayers"
        >
      >,
    ) => {
      const base = ensureSceneDraft();
      if (!base) return;
      const next: FloorplanScene = { ...base, ...patch };
      commitSceneLocal(next);
    },
    [ensureSceneDraft, commitSceneLocal],
  );

  const rotatePinLabelsGlobal = (dir: 1 | -1) => {
    const next = normalizePinLabelRot(pinLabelRotationDeg + dir * 90);
    setPinLabelRotationDeg(next);
    patchSceneMeta({ pinLabelRotationDeg: next });
  };

  const rotateSelectedPinLabel = (dir: 1 | -1) => {
    if (!id || !selectedCompany) return;
    const cur = companiesRef.current.find((c) => c.id === selectedCompany);
    if (!cur) return;
    const next = normalizePinLabelRot((cur.mapLabelRot ?? 0) + dir * 90);
    setCompanies((prev) =>
      prev.map((c) => (c.id === selectedCompany ? { ...c, mapLabelRot: next } : c)),
    );
    void (async () => {
      try {
        const updated = await mapService.updateBooth(id, selectedCompany, {
          mapLabelRot: next,
        });
        setCompanies((prev) =>
          prev.map((c) => (c.id === selectedCompany ? { ...c, ...updated } : c)),
        );
      } catch {
        setSaveError("Не удалось сохранить поворот надписи");
      }
    })();
  };

  /** Rotate label on a custom scheme marker (вход / офис / зал…). */
  const rotateSelectedMarkerLabel = (dir: 1 | -1) => {
    const base = sceneDraftRef.current;
    if (!base || !selectedMarkerId) return;
    const obj = base.objects.find((o) => o.id === selectedMarkerId);
    if (!obj) return;
    const next = normalizePinLabelRot((obj.labelRot ?? 0) + dir * 90);
    commitSceneLocal({
      ...base,
      objects: base.objects.map((o) =>
        o.id === selectedMarkerId ? { ...o, labelRot: next } : o,
      ),
    });
  };

  const toggleUnderlayLayer = (id: UnderlaySvgLayerId) => {
    const cur = resolveUnderlaySvgLayers(underlayLayers);
    const next = { ...cur, [id]: !cur[id] };
    setUnderlayLayers(next);
    patchSceneMeta({ underlayLayers: next });
  };

  const underlaySvgDisplay = useMemo(() => {
    if (!showUnderlay || !floorplanSvg) return "";
    return applyUnderlaySvgLayerVisibility(floorplanSvg, underlayLayers);
  }, [showUnderlay, floorplanSvg, underlayLayers]);

  const toggleUnderlay = () => {
    if (!hasUnderlay) return;
    const next = !showUnderlay;
    setShowUnderlay(next);
    patchSceneMeta({ underlayVisible: next });
  };

  const changeLabelScale = (value: number) => {
    const v = Math.min(2.2, Math.max(0.7, Number(value.toFixed(2))));
    setLabelScale(v);
    const base = ensureSceneDraft();
    if (!base) return;
    const next: FloorplanScene = { ...base, labelScale: v };
    commitSceneLocal(next, { recordUndo: false });
  };

  const openMoreMenu = () => {
    const btn = moreBtnRef.current;
    if (!btn) {
      setMoreOpen((v) => !v);
      return;
    }
    if (moreOpen) {
      setMoreOpen(false);
      setMoreMenuPos(null);
      return;
    }
    const r = btn.getBoundingClientRect();
    const menuH = 220;
    const openUp = r.bottom + menuH > window.innerHeight - 8;
    setMoreMenuPos({
      top: openUp ? Math.max(8, r.top - menuH - 4) : r.bottom + 4,
      right: Math.max(8, window.innerWidth - r.right),
    });
    setMoreOpen(true);
  };

  const selectMarker = (id: string | null) => {
    setSelectedMarkerId(id);
    // Only clear stamp when picking an existing marker — never when
    // deselecting / activating a tool (that used to kill every stamp click).
    if (id) {
      setMarkerToolId(null);
      clearCompanySelection();
      setPlaceMode(false);
    }
    if (!id) {
      setMarkerLabelDraft("");
      setMarkerCodeDraft("");
      return;
    }
    const obj = (sceneDraftRef.current?.objects ?? schemeMarkers).find((o) => o.id === id);
    setMarkerLabelDraft(
      obj?.kind === "booth"
        ? splitExhibitorNames(obj.label).join("\n")
        : (obj?.label ?? ""),
    );
    setMarkerCodeDraft(obj?.code ?? "");
    // Pan/zoom only when finding from the sidebar list — not on every canvas select.
  };

  const placeMarkerAt = (x: number, y: number, toolId: string) => {
    const base = ensureSceneDraft();
    if (!base) return;
    const tool = MARKER_TOOLS.find((t) => t.id === toolId) ?? MARKER_TOOLS[MARKER_TOOLS.length - 1]!;
    const kind = tool.kind;
    const obj: FloorplanSceneObject = {
      id: newMarkerId(kind),
      kind,
      code: kind === "booth" ? "" : undefined,
      label:
        kind === "booth"
          ? ""
          : kind === "event_link"
            ? "Выберите выставку"
            : tool.label,
      showInApp: kind !== "booth",
      linkArrow: kind === "event_link" ? "e" : undefined,
      x: Number(Math.max(0, Math.min(100 - tool.w, x - tool.w / 2)).toFixed(2)),
      y: Number(Math.max(0, Math.min(100 - tool.h, y - tool.h / 2)).toFixed(2)),
      w: tool.w,
      h: tool.h,
    };
    const next: FloorplanScene = { ...base, objects: [...base.objects, obj] };
    commitSceneLocal(next);
    setShowOverlay(true);
    setSelectedMarkerId(obj.id);
    // Keep stamp armed for multi-place; Esc / повторный клик по кнопке снимет.
    setMarkerCodeDraft(obj.code ?? "");
    setMarkerLabelDraft(obj.kind === "booth" ? "" : (obj.label ?? ""));
  };

  const updateSelectedMarkerSize = (axis: "w" | "h", value: number) => {
    const base = sceneDraftRef.current;
    if (!base || !selectedMarkerId) return;
    const obj = base.objects.find((o) => o.id === selectedMarkerId);
    if (!obj || obj.locked) return;
    const nextVal = Math.max(1.2, Math.min(100, Number(value.toFixed(2))));
    let { x, y, w, h } = obj;
    if (axis === "w") {
      w = nextVal;
      x = Math.max(0, Math.min(100 - w, x));
    } else {
      h = nextVal;
      y = Math.max(0, Math.min(100 - h, y));
    }
    const next: FloorplanScene = {
      ...base,
      objects: base.objects.map((o) =>
        o.id === selectedMarkerId
          ? {
              ...o,
              x: Number(x.toFixed(2)),
              y: Number(y.toFixed(2)),
              w: Number(w.toFixed(2)),
              h: Number(h.toFixed(2)),
            }
          : o,
      ),
    };
    commitSceneLocal(next, { recordUndo: false });
  };

  const removeSelectedMarker = () => {
    const base = sceneDraftRef.current;
    if (!base || !selectedMarkerId) return;
    const next: FloorplanScene = {
      ...base,
      objects: base.objects.filter((o) => o.id !== selectedMarkerId),
    };
    selectMarker(null);
    commitSceneLocal(next);
  };

  const clearSchemeMarkers = () => {
    const base = ensureSceneDraft();
    if (!base || !base.objects.length) return;
    const ok = window.confirm(
      "Удалить все маркеры на схеме (включая экспонентов)? Подложка останется.",
    );
    if (!ok) return;
    const next: FloorplanScene = { ...base, objects: [] };
    selectMarker(null);
    commitSceneLocal(next);
  };

  /**
   * Remove legacy booth markers that duplicate exhibitor pins.
   * Keeps technical objects (office / toilet / entrance…).
   */
  const clearBoothMarkers = async () => {
    const base = ensureSceneDraft();
    if (!base) return;
    const booths = base.objects.filter((o) => o.kind === "booth");
    if (!booths.length) {
      setSaveError("Маркеров-стендов уже нет — только тех. объекты и пины экспонентов");
      return;
    }
    const ok = window.confirm(
      `Удалить ${booths.length} маркеров-стендов?\n` +
        `Экспоненты останутся как пины (список «Экспоненты»).\n` +
        `Тех. объекты (офис, туалет, вход…) сохранятся.`,
    );
    if (!ok) return;
    const layers = {
      ...resolveUnderlaySvgLayers(underlayLayers),
      texts: false,
      fills: false,
      contours: false,
    };
    const next: FloorplanScene = {
      ...base,
      objects: base.objects.filter((o) => o.kind !== "booth"),
      underlayLayers: layers,
      underlayVisible: true,
    };
    setUnderlayLayers(layers);
    setShowSchemeBooths(false);
    selectMarker(null);
    commitSceneLocal(next);
    await persistSceneNow(next);
    setSaveError(`Убрано маркеров-стендов: ${booths.length}. Экспоненты = пины.`);
  };

  const updateSelectedMarkerLabel = (label: string) => {
    setMarkerLabelDraft(label);
    const base = sceneDraftRef.current;
    if (!base || !selectedMarkerId) return;
    const obj = base.objects.find((o) => o.id === selectedMarkerId);
    const stored =
      obj?.kind === "booth" ? joinExhibitorNames(splitExhibitorNames(label)) : label.trim();
    const next: FloorplanScene = {
      ...base,
      objects: base.objects.map((o) =>
        o.id === selectedMarkerId ? { ...o, label: stored || o.label } : o,
      ),
    };
    commitSceneLocal(next, { recordUndo: false });
  };

  const updateSelectedEventLink = (linkEventId: string) => {
    const base = sceneDraftRef.current;
    if (!base || !selectedMarkerId) return;
    const sib = groupSiblings.find((s) => s.id === linkEventId);
    const obj = base.objects.find((o) => o.id === selectedMarkerId);
    const arrow: FloorplanLinkArrow = obj?.linkArrow ?? "e";
    const label = sib ? eventLinkLabel(sib.title, arrow) : obj?.label ?? "Выберите выставку";
    const linkAccent = sib?.primaryColor;
    setMarkerLabelDraft(label);
    const next: FloorplanScene = {
      ...base,
      objects: base.objects.map((o) =>
        o.id === selectedMarkerId
          ? {
              ...o,
              kind: "event_link",
              linkEventId: linkEventId || undefined,
              linkArrow: arrow,
              linkAccent,
              label,
            }
          : o,
      ),
    };
    commitSceneLocal(next);
  };

  const updateSelectedLinkArrow = (linkArrow: FloorplanLinkArrow) => {
    const base = sceneDraftRef.current;
    if (!base || !selectedMarkerId) return;
    const obj = base.objects.find((o) => o.id === selectedMarkerId);
    if (!obj || obj.kind !== "event_link") return;
    const sib = groupSiblings.find((s) => s.id === obj.linkEventId);
    const label = sib ? eventLinkLabel(sib.title, linkArrow) : obj.label;
    if (label) setMarkerLabelDraft(label);
    const next: FloorplanScene = {
      ...base,
      objects: base.objects.map((o) =>
        o.id === selectedMarkerId
          ? { ...o, linkArrow, ...(label ? { label } : {}) }
          : o,
      ),
    };
    commitSceneLocal(next);
  };

  const updateSelectedMarkerCode = (code: string) => {
    setMarkerCodeDraft(code);
    const base = sceneDraftRef.current;
    if (!base || !selectedMarkerId) return;
    const next: FloorplanScene = {
      ...base,
      objects: base.objects.map((o) =>
        o.id === selectedMarkerId
          ? { ...o, code: normalizeBoothCode(code) || undefined }
          : o,
      ),
    };
    commitSceneLocal(next, { recordUndo: false });
  };

  const toggleSelectedMarkerShowInApp = () => {
    const base = sceneDraftRef.current;
    if (!base || !selectedMarkerId) return;
    const obj = base.objects.find((o) => o.id === selectedMarkerId);
    if (!obj) return;
    const nextVal = obj.showInApp === false;
    const next: FloorplanScene = {
      ...base,
      objects: base.objects.map((o) =>
        o.id === selectedMarkerId ? { ...o, showInApp: nextVal } : o,
      ),
    };
    commitSceneLocal(next);
  };

  const onMarkerMoveStart = (markerId: string, clientX: number, clientY: number) => {
    const base = sceneDraftRef.current;
    const orig = base?.objects.find((o) => o.id === markerId);
    if (!orig || !base || orig.locked) return;
    pushSceneUndo(base);
    markerDrag.current = {
      id: markerId,
      mode: "move",
      startX: clientX,
      startY: clientY,
      orig: { ...orig },
    };
  };

  const onMarkerResizeStart = (
    markerId: string,
    corner: MarkerResizeCorner,
    clientX: number,
    clientY: number,
  ) => {
    const base = sceneDraftRef.current;
    const orig = base?.objects.find((o) => o.id === markerId);
    if (!orig || !base || orig.locked) return;
    pushSceneUndo(base);
    markerDrag.current = {
      id: markerId,
      mode: "resize",
      corner,
      startX: clientX,
      startY: clientY,
      orig: { ...orig },
    };
  };

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const drag = markerDrag.current;
      const base = sceneDraftRef.current;
      if (!drag || !base) return;
      const { w: boardW, h: boardH } = boardSizeRef.current;
      const { dx: dxPct, dy: dyPct } = screenDeltaToBoardPct(
        e.clientX - drag.startX,
        e.clientY - drag.startY,
        boardW,
        boardH,
        boardRotationRef.current,
        viewZoomRef.current,
      );
      let nextObj: FloorplanSceneObject = { ...drag.orig };
      if (drag.mode === "move") {
        nextObj.x = Number(Math.max(0, Math.min(100 - drag.orig.w, drag.orig.x + dxPct)).toFixed(2));
        nextObj.y = Number(Math.max(0, Math.min(100 - drag.orig.h, drag.orig.y + dyPct)).toFixed(2));
      } else {
        const c = drag.corner ?? "se";
        let { x, y, w, h } = drag.orig;
        if (c.includes("e")) w = drag.orig.w + dxPct;
        if (c.includes("s")) h = drag.orig.h + dyPct;
        if (c.includes("w")) {
          w = drag.orig.w - dxPct;
          x = drag.orig.x + dxPct;
        }
        if (c.includes("n")) {
          h = drag.orig.h - dyPct;
          y = drag.orig.y + dyPct;
        }
        w = Math.max(1.2, Math.min(100, w));
        h = Math.max(1.2, Math.min(100, h));
        x = Math.max(0, Math.min(100 - w, x));
        y = Math.max(0, Math.min(100 - h, y));
        nextObj = {
          ...drag.orig,
          x: Number(x.toFixed(2)),
          y: Number(y.toFixed(2)),
          w: Number(w.toFixed(2)),
          h: Number(h.toFixed(2)),
        };
      }
      setSceneDraft({
        ...base,
        objects: base.objects.map((o) => (o.id === drag.id ? nextObj : o)),
      });
    };
    const onUp = () => {
      if (!markerDrag.current) return;
      markerDrag.current = null;
      setSceneDirty(true);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);

  const switchWorkMode = (mode: WorkMode) => {
    setWorkMode(mode);
    setCropMode(false);
    setPlaceMode(false);
    setEditMode(mode === "placement");
    setMarkerToolId(null);
    setSelectedMarkerId(null);
    if (mode === "scheme") {
      setShowUnderlay(true);
      setShowOverlay(true);
      clearCompanySelection();
    } else {
      setShowUnderlay(true);
      setShowOverlay(true);
    }
  };

  const rotatePlan = async (quarterTurns: 1 | -1) => {
    if (!id || !activeHallObj || !hasPlan) return;
    setUploadingSvg(true);
    setSaveError("");
    try {
      // Visual-only: underlay SVG + pins stay aligned (CSS rotation).
      const hall = await mapService.rotateFloorplan(
        id,
        activeHallObj.id,
        quarterTurns,
        true,
      );
      applyHallUpdate(hall);
      const rot = hall.floorplanScene?.rotationDeg ?? 0;
      setBoardRotation(rot === 90 || rot === 180 || rot === 270 ? rot : 0);
    } catch {
      setSaveError("Не удалось повернуть схему");
    } finally {
      setUploadingSvg(false);
    }
  };

  const fitPlan = async () => {
    if (!id || !activeHallObj || !hasPlan) return;
    if (hasUnderlay) {
      setSaveError(
        "Подложка Corel 1:1 — поля листа убирайте «Обрезкой», не «По содержимому» (иначе съедет выравнивание).",
      );
      setCropMode(true);
      return;
    }
    setUploadingSvg(true);
    setSaveError("");
    try {
      const hall = await mapService.fitFloorplan(id, activeHallObj.id);
      applyHallUpdate(hall);
      setBoardScale(100);
      const n = hall.floorplanScene?.objects.length ?? 0;
      setSaveError(`Схема подогнана: ${n} объектов, ${hall.floorplanWidth}×${hall.floorplanHeight}`);
    } catch {
      setSaveError("Не удалось подогнать схему по содержимому");
    } finally {
      setUploadingSvg(false);
    }
  };

  const clearPlan = async () => {
    if (!id || !activeHallObj || !hasPlan) return;
    const ok = window.confirm(
      "Удалить схему павильона? Пины компаний останутся — их можно расставить заново после загрузки.",
    );
    if (!ok) return;
    setUploadingSvg(true);
    setSaveError("");
    try {
      const hall = await mapService.clearFloorplan(id, activeHallObj.id);
      applyHallUpdate(hall);
      setBoardScale(100);
    } catch {
      setSaveError("Не удалось удалить схему");
    } finally {
      setUploadingSvg(false);
    }
  };

  if (eventLoading || loading) {
    return (
      <div className="p-6 flex justify-center text-gray-400">
        <Loader2 size={28} className="animate-spin" />
      </div>
    );
  }

  if (!event) return <div className="p-6 text-gray-500">Мероприятие не найдено</div>;

  const renderPins = () => {
    const inv = 1 / Math.max(0.05, viewZoom);
    const borderPx = Math.max(0.35, Number((1 * inv).toFixed(3)));
    return adminPinGroups.map((group) => {
      const primary =
        group.companies.find((c) => c.id === selectedCompany) ?? group.companies[0]!;
      const i = companies.findIndex((x) => x.id === primary.id);
      const color = pinColorFor(primary, Math.max(0, i));
      const mateIds = group.companies.map((c) => c.id);
      const selected =
        group.companies.some((c) => c.id === selectedCompany) ||
        group.companies.some((c) => selectedIds.includes(c.id));
      const locked = group.companies.some((c) => c.mapLocked === true);
      const isGroup = group.companies.length > 1;
      const handles: Array<{
        id: ResizeHandle;
        left?: number | string;
        right?: number | string;
        top?: number | string;
        bottom?: number | string;
        cursor: string;
        tx?: string;
        ty?: string;
      }> = (
        [
          { id: "nw" as const, left: -8, top: -8 },
          { id: "ne" as const, right: -8, top: -8 },
          { id: "sw" as const, left: -8, bottom: -8 },
          { id: "se" as const, right: -8, bottom: -8 },
          { id: "n" as const, left: "50%", top: -8, tx: "-50%" },
          { id: "s" as const, left: "50%", bottom: -8, tx: "-50%" },
          { id: "w" as const, left: -8, top: "50%", ty: "-50%" },
          { id: "e" as const, right: -8, top: "50%", ty: "-50%" },
        ] as const
      ).map((h) => ({
        ...h,
        cursor: cursorForHandle(h.id, boardRotation),
      }));
      // Screen-align chrome (lock / resize handles) when "Надписи прямо" is on.
      const upright = normalizePinLabelRot(
        labelsUpright && boardRotation ? -boardRotation : 0,
      );
      const nameRot = normalizePinLabelRot(
        upright + pinLabelRotationDeg + (primary.mapLabelRot ?? 0),
      );
      const boothText = (group.booth || primary.booth?.trim() || "").trim();
      const box = { left: group.left, top: group.top, w: group.w, h: group.h };
      const caption = pinCaption(primary);
      const nameLines = (
        isGroup ? Math.min(4, group.companies.length) : pinNameLines(primary.mapLabelLines)
      ) as 1 | 2 | 3 | 4;
      // Live draft while dragging the per-pin slider; never cap at 1 (slider 50–250%).
      const perPinScale =
        primary.id === selectedCompany ? mapLabelScaleDraft : primary.mapLabelScale;
      const scale = pinEffectiveScale(labelScale, perPinScale);
      const names = isGroup
        ? group.companies.map((c) => pinCaption(c))
        : caption
          ? [caption]
          : [];
      const { textPx } = fitPinLabel({
        wPct: box.w,
        hPct: box.h,
        boardW: board.width,
        boardH: board.height,
        labelScale: scale,
        nameLines,
        nameRot,
        boothCode: boothText,
        nameText: names.join(" "),
      });

      return (
        <div
          key={group.key}
          data-map-pin
          {...(editMode ? { "data-map-no-pan": "" } : {})}
          onPointerDown={onPinPointerDown(primary.id, mateIds)}
          onClick={(e) => {
            if (editMode) return;
            if (e.shiftKey) {
              selectCompany(primary.id, { additive: true });
              setEditMode(true);
              setCropMode(false);
              return;
            }
            if (primary.id === selectedCompany && !isGroup) {
              clearCompanySelection();
              return;
            }
            selectCompany(primary.id);
            setEditMode(true);
            setCropMode(false);
          }}
          className={`absolute touch-none overflow-hidden ${
            locked
              ? "cursor-default"
              : editMode
                ? "cursor-grab active:cursor-grabbing"
                : "cursor-pointer"
          }`}
          style={{
            left: `${box.left}%`,
            top: `${box.top}%`,
            width: `${box.w}%`,
            height: `${box.h}%`,
            zIndex: selected ? 40 : boothText ? 12 : 6,
            borderRadius: 6,
            borderStyle: "solid",
            borderWidth: borderPx,
            borderColor: selected ? "#4f46e5" : locked ? "#94a3b8" : color,
            backgroundColor: selected
              ? "rgba(79,70,229,0.28)"
              : locked
                ? "rgba(148,163,184,0.28)"
                : `${color}55`,
            boxShadow: selected
              ? "0 0 0 3px rgba(79,70,229,0.55), 0 0 16px rgba(79,70,229,0.35)"
              : "none",
            outline: "none",
            fontFamily: "system-ui, Segoe UI, sans-serif",
          }}
          title={
            isGroup
              ? `${boothText || "—"} · ${group.companies.length} компаний`
              : `${primary.name} · ${boothText || "—"}${locked ? " · закреплён" : ""}`
          }
        >
          <PinCardLabels
            boothCode={boothText}
            names={names}
            boxWPct={box.w}
            boxHPct={box.h}
            boardW={board.width}
            boardH={board.height}
            boardRotation={boardRotation}
            nameRotation={nameRot}
            nameLines={nameLines}
            labelScale={scale}
            renderName={
              isGroup
                ? (line, idx) => {
                    const c = group.companies[idx]!;
                    const active = c.id === selectedCompany;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        data-map-no-pan=""
                        onPointerDown={(e) => {
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          selectCompany(c.id);
                          setEditMode(true);
                          setCropMode(false);
                        }}
                        className={`text-center leading-tight rounded px-0.5 truncate w-full ${
                          active
                            ? "bg-indigo-600/25 ring-1 ring-indigo-500"
                            : "hover:bg-white/40"
                        }`}
                        style={{
                          color: "#0f172a",
                          fontSize: Math.max(4, textPx * 0.92),
                          fontWeight: 600,
                          lineHeight: 1.15,
                          textShadow: "0 0 2px rgba(255,255,255,0.9)",
                        }}
                        title={line}
                      >
                        {line}
                      </button>
                    );
                  }
                : undefined
            }
          />

          {locked && (
            <span
              className="absolute z-30 w-3.5 h-3.5 rounded-full bg-slate-700 text-white flex items-center justify-center shadow-sm"
              style={{
                top: 0,
                right: 0,
                transform: `translate(40%, -40%) rotate(${upright}deg) scale(${inv})`,
                transformOrigin: "center",
              }}
            >
              <Lock size={8} strokeWidth={2.5} />
            </span>
          )}
          {selected && editMode && !locked &&
            handles.map((h) => (
              <span
                key={h.id}
                onPointerDown={onResizePointerDown(primary.id, h.id, mateIds, box)}
                className="absolute z-30 flex items-center justify-center"
                style={{
                  width: 16,
                  height: 16,
                  left: h.left,
                  right: h.right,
                  top: h.top,
                  bottom: h.bottom,
                  transform: `translate(${h.tx ?? "0"}, ${h.ty ?? "0"}) rotate(${upright}deg) scale(${inv})`,
                  transformOrigin: "center",
                  cursor: h.cursor,
                }}
              >
                <span
                  className="block pointer-events-none"
                  style={{
                    width: 8,
                    height: 8,
                    background: "#111827",
                    border: "1.5px solid #fff",
                    borderRadius: 1,
                    boxShadow: "0 0 0 1px rgba(0,0,0,0.4)",
                  }}
                />
              </span>
            ))}
        </div>
      );
    });
  };

  const detailPanel = selectedComp ? (
    <div className="w-full md:w-64 bg-white border-l border-gray-100 flex flex-col flex-shrink-0">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
        <span className="text-[12px] text-gray-700">Стенд на карте</span>
        <button
          type="button"
          onClick={() => clearCompanySelection()}
          className="p-1 rounded-md hover:bg-gray-100 text-gray-400"
        >
          <X size={14} />
        </button>
      </div>
      <div className="p-4 flex-1 overflow-y-auto space-y-3">
        {selectedIds.length > 1 && (
          <div className="rounded-lg bg-indigo-50 border border-indigo-100 px-2.5 py-2 text-[11px] text-indigo-800">
            Выбрано объектов: <b>{selectedIds.length}</b>
            <span className="text-indigo-500"> · Shift+клик добавляет</span>
          </div>
        )}
        <div className="flex items-start gap-2.5">
          <CompanyAvatar name={selectedComp.name} logoUrl={selectedComp.logoUrl} size="md" />
          <div className="min-w-0">
            <div className="text-[13px] text-gray-900 truncate">{selectedComp.name}</div>
            <div className="text-[11px] text-gray-400 truncate">{selectedComp.category}</div>
          </div>
        </div>

        <div>
          <label className="text-[11px] text-gray-500 mb-1 block">Номер стенда</label>
          <input
            value={boothDraft}
            onChange={(e) => setBoothDraft(e.target.value)}
            onBlur={() => void saveBoothMeta()}
            placeholder="А20 / К723 / 10"
            className="w-full px-2.5 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400"
          />
        </div>

        <div>
          <label className="text-[11px] text-gray-500 mb-1 block">Название на пине</label>
          <textarea
            value={mapLabelDraft}
            onChange={(e) => setMapLabelDraft(e.target.value)}
            onBlur={() => void savePinLabel()}
            rows={2}
            placeholder={selectedComp.name}
            className="w-full px-2.5 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400 resize-y"
          />
          <div className="mt-1.5 flex items-center gap-1.5">
            <span className="text-[11px] text-gray-500">Строк:</span>
            {([1, 2, 3] as const).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => void savePinLabelLines(n)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium border ${
                  mapLabelLinesDraft === n
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-slate-700 border-gray-200 hover:bg-gray-50"
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          <label className="mt-2 block text-[11px] text-gray-500">
            Размер текста: {Math.round(mapLabelScaleDraft * 100)}%
            <input
              type="range"
              min={50}
              max={250}
              step={5}
              value={Math.round(mapLabelScaleDraft * 100)}
              onChange={(e) => setMapLabelScaleDraft(Number(e.target.value) / 100)}
              onPointerUp={(e) =>
                void savePinLabelScale(Number((e.target as HTMLInputElement).value) / 100)
              }
              className="mt-1 w-full accent-indigo-600"
            />
          </label>
          <p className="mt-1 text-[10px] text-gray-400 leading-snug">
            Номер стенда — в углу. Название по центру, перенос и обрезка «…».
            Пустое поле — название компании.
          </p>
        </div>

        <div>
          <label className="text-[11px] text-gray-500 mb-1.5 block">Цвет объекта</label>
          <div className="flex flex-wrap gap-1.5">
            {PIN_COLORS.map((col) => (
              <button
                key={col}
                type="button"
                onClick={() => {
                  setColorDraft(col);
                  void (async () => {
                    if (!id || !selectedCompany) return;
                    try {
                      const updated = await mapService.updateBooth(id, selectedCompany, {
                        pinColor: col,
                      });
                      setCompanies((prev) =>
                        prev.map((c) => (c.id === selectedCompany ? { ...c, ...updated } : c)),
                      );
                    } catch {
                      setSaveError("Не удалось сохранить цвет");
                    }
                  })();
                }}
                className={`w-6 h-6 rounded-full border-2 ${
                  colorDraft === col ? "border-gray-900 scale-110" : "border-white shadow"
                }`}
                style={{ backgroundColor: col }}
                aria-label={col}
              />
            ))}
          </div>
        </div>

        <div>
          <label className="text-[11px] text-gray-500 mb-1.5 block">Поворот надписи</label>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => rotateSelectedPinLabel(-1)}
              className="flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
              title="Надпись −90°"
            >
              <RotateCcw size={14} />
            </button>
            <span className="flex-1 text-center text-[12px] tabular-nums text-slate-700 font-medium">
              {normalizePinLabelRot(selectedComp.mapLabelRot)}°
            </span>
            <button
              type="button"
              onClick={() => rotateSelectedPinLabel(1)}
              className="flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
              title="Надпись +90°"
            >
              <RotateCw size={14} />
            </button>
          </div>
          <p className="mt-1 text-[10px] text-gray-400 leading-snug">
            Только этот экспонент. Общий поворот — кнопки у «Надписи прямо».
          </p>
        </div>

        <div className="rounded-lg bg-slate-50 border border-slate-100 p-2.5 space-y-2">
          <p className="text-[11px] text-slate-600 leading-snug">
            Объект — <b>прямоугольник</b> по размеру стенда. Включите «Редактирование»,
            тяните за углы, либо нажмите «Подогнать по стенду». Закрепите готовые —
            их нельзя схватить случайно.
          </p>
          {isPlaced(selectedComp) && (
            <div className="grid grid-cols-2 gap-2">
              <label className="text-[10px] text-gray-500 block">
                Ширина %
                <input
                  type="number"
                  min={1.2}
                  max={80}
                  step={0.1}
                  disabled={selectedComp.mapLocked === true}
                  value={Number((selectedComp.mapPoint.w ?? DEFAULT_PIN_W).toFixed(1))}
                  onChange={(e) => {
                    const w = Math.min(80, Math.max(1.2, Number(e.target.value) || DEFAULT_PIN_W));
                    setCompanies((prev) =>
                      prev.map((c) =>
                        c.id === selectedComp.id
                          ? { ...c, mapPoint: { ...c.mapPoint, w } }
                          : c,
                      ),
                    );
                  }}
                  onBlur={() => {
                    const cur = companiesRef.current.find((c) => c.id === selectedComp.id);
                    if (!cur || cur.mapLocked) return;
                    void placePin(cur.id, cur.mapPoint.x, cur.mapPoint.y, {
                      mapW: cur.mapPoint.w ?? DEFAULT_PIN_W,
                      mapH: cur.mapPoint.h ?? DEFAULT_PIN_H,
                      snap: false,
                    });
                  }}
                  className="mt-0.5 w-full px-2 py-1.5 rounded-md border border-gray-200 text-[12px] font-mono outline-none focus:border-indigo-400 disabled:bg-gray-100 disabled:text-gray-400"
                />
              </label>
              <label className="text-[10px] text-gray-500 block">
                Высота %
                <input
                  type="number"
                  min={1.2}
                  max={80}
                  step={0.1}
                  disabled={selectedComp.mapLocked === true}
                  value={Number((selectedComp.mapPoint.h ?? DEFAULT_PIN_H).toFixed(1))}
                  onChange={(e) => {
                    const h = Math.min(80, Math.max(1.2, Number(e.target.value) || DEFAULT_PIN_H));
                    setCompanies((prev) =>
                      prev.map((c) =>
                        c.id === selectedComp.id
                          ? { ...c, mapPoint: { ...c.mapPoint, h } }
                          : c,
                      ),
                    );
                  }}
                  onBlur={() => {
                    const cur = companiesRef.current.find((c) => c.id === selectedComp.id);
                    if (!cur || cur.mapLocked) return;
                    void placePin(cur.id, cur.mapPoint.x, cur.mapPoint.y, {
                      mapW: cur.mapPoint.w ?? DEFAULT_PIN_W,
                      mapH: cur.mapPoint.h ?? DEFAULT_PIN_H,
                      snap: false,
                    });
                  }}
                  className="mt-0.5 w-full px-2 py-1.5 rounded-md border border-gray-200 text-[12px] font-mono outline-none focus:border-indigo-400 disabled:bg-gray-100 disabled:text-gray-400"
                />
              </label>
            </div>
          )}
          <div className="text-[10px] text-gray-400 font-mono">
            центр x: {selectedComp.mapPoint.x.toFixed(1)}% · y: {selectedComp.mapPoint.y.toFixed(1)}%
          </div>
        </div>

        {isPlaced(selectedComp) && (
          <button
            type="button"
            onClick={() => void setMapLocked(selectedComp.id, !selectedComp.mapLocked)}
            className={`w-full py-2 rounded-lg text-[12px] font-medium flex items-center justify-center gap-1.5 ${
              selectedComp.mapLocked
                ? "bg-slate-700 hover:bg-slate-800 text-white"
                : "bg-slate-100 hover:bg-slate-200 text-slate-700"
            }`}
          >
            {selectedComp.mapLocked ? <Unlock size={14} /> : <Lock size={14} />}
            {selectedComp.mapLocked ? "Открепить" : "Закрепить"}
          </button>
        )}

        {isPlaced(selectedComp) && (
          <button
            type="button"
            disabled={selectedComp.mapLocked === true}
            onClick={() => {
              setEditMode(true);
              setCropMode(false);
              void snapSelectedToBooth();
            }}
            className="w-full py-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[12px] font-medium disabled:opacity-40 disabled:pointer-events-none"
          >
            Подогнать по стенду на схеме
          </button>
        )}

        {isPlaced(selectedComp) && (selectedComp.booth || "").trim() && (
          <>
            <button
              type="button"
              disabled={selectedComp.mapLocked === true}
              onClick={() => {
                setEditMode(true);
                setCropMode(false);
                void gatherBoothMates();
              }}
              className="w-full py-2 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-[12px] font-medium disabled:opacity-40 disabled:pointer-events-none"
              title="Одна карточка с номером стенда; внутри кликабельны названия компаний"
            >
              Собрать групповую карточку
            </button>
            <button
              type="button"
              disabled={selectedComp.mapLocked === true}
              onClick={() => {
                setEditMode(true);
                setCropMode(false);
                void spreadBoothMates();
              }}
              className="w-full py-2 rounded-lg bg-violet-50 hover:bg-violet-100 text-violet-700 text-[12px] font-medium disabled:opacity-40 disabled:pointer-events-none"
              title="Разложить компании полосками внутри контура стенда"
            >
              Разложить полосками
            </button>
          </>
        )}

        <button
          type="button"
          disabled={selectedComp.mapLocked === true && isPlaced(selectedComp)}
          onClick={() => {
            if (selectedComp.mapLocked) {
              setSaveError("Стенд закреплён — сначала открепите");
              return;
            }
            setEditMode(true);
            setCropMode(false);
            setPlaceMode(true);
          }}
          className={`w-full py-2.5 rounded-lg text-[12px] font-medium flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:pointer-events-none ${
            placeMode
              ? "bg-amber-500 text-white"
              : "bg-indigo-600 hover:bg-indigo-700 text-white"
          }`}
        >
          <MapIcon size={14} />
          {placeMode
            ? "Кликните по стенду на карте…"
            : isPlaced(selectedComp)
              ? "Переместить / клик-snap"
              : "Отобразить на карте"}
        </button>

        {isPlaced(selectedComp) || selectedIds.length > 1 ? (
          <button
            type="button"
            onClick={() =>
              void removePinsFromMap(
                selectedIds.length > 1 ? selectedIds : [selectedComp.id],
              )
            }
            className="w-full py-2.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 text-[12px] font-medium flex items-center justify-center gap-1.5 border border-red-100"
            title="Delete / Backspace — убрать с карты"
          >
            <Trash2 size={14} />
            {selectedIds.length > 1
              ? `Убрать с карты (${selectedIds.length})`
              : "Убрать с карты"}
          </button>
        ) : null}

        <button
          type="button"
          onClick={() => navigate(`/events/${id}/exhibitors/${selectedComp.id}`)}
          className="w-full py-2 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-[12px]"
        >
          Открыть карточку →
        </button>
      </div>
    </div>
  ) : null;

  const rotatedBoard =
    boardRotation === 90 || boardRotation === 270
      ? { width: board.height, height: board.width }
      : board;

  const mapCanvas = (
    <MapViewport
      ref={mapViewportRef}
      className="flex-1 min-h-0"
      boardWidth={rotatedBoard.width}
      boardHeight={rotatedBoard.height}
      minZoomRatio={0.25}
      fitKey={`${activeHall}:${floorplanSvg.length}:${board.width}x${board.height}:${boardRotation}:${cropDraft.left}-${cropDraft.top}-${cropDraft.right}-${cropDraft.bottom}`}
      onTransform={(t) => {
        setViewZoom((prev) => (Math.abs(prev - t.zoom) < 0.002 ? prev : t.zoom));
        const fitZ = mapViewportRef.current?.getFitZoom() ?? 0;
        if (fitZ > 0.001) {
          const pct = Math.round((t.zoom / fitZ) * 100);
          setViewZoomPct((prev) => (prev === pct ? prev : Math.max(25, Math.min(400, pct))));
        }
      }}
    >
      <div
        ref={mapFrameRef}
        className="relative w-full h-full bg-white border border-gray-300 shadow-sm overflow-hidden"
        style={{ width: rotatedBoard.width, height: rotatedBoard.height }}
      >
        <div
          ref={mapSurfaceRef}
          className="absolute origin-center"
          {...(markerToolId ? { "data-map-no-pan": "" } : {})}
          style={{
            width: board.width,
            height: board.height,
            left: "50%",
            top: "50%",
            transform: `translate(-50%, -50%) rotate(${boardRotation}deg)`,
            cursor: markerToolId ? "crosshair" : undefined,
          }}
          onClick={(e) => {
            if (cropMode || pinDrag.current || markerDrag.current) return;
            if (!mapFrameRef.current) return;
            const pct = pointerOnBoard(e.clientX, e.clientY);
            // Objects (office / entrance / toilet…) live in placement, on top of underlay.
            if (workMode === "placement" && markerToolId) {
              setShowOverlay(true);
              placeMarkerAt(pct.x, pct.y, markerToolId);
              return;
            }
            if (workMode === "scheme") {
              return;
            }
            if (selectedMarkerId) {
              selectMarker(null);
              return;
            }
            if (!placeMode || !selectedCompany) return;
            void placePin(selectedCompany, pct.x, pct.y);
          }}
        >
          {showUnderlay && hasUnderlay && underlaySvgDisplay ? (
            <div
              className={`absolute [&>svg]:!block [&>svg]:!w-full [&>svg]:!h-full [&>svg]:!max-w-none [&>svg]:!max-h-none ${
                markerToolId ? "pointer-events-none" : ""
              }`}
              style={(() => {
                const pad =
                  sceneDraft?.contentPad ?? activeHallObj?.floorplanScene?.contentPad;
                if (!pad || !(pad.left || pad.top || pad.right || pad.bottom)) {
                  return { inset: 0 };
                }
                return {
                  left: `${pad.left}%`,
                  top: `${pad.top}%`,
                  right: `${pad.right}%`,
                  bottom: `${pad.bottom}%`,
                };
              })()}
              dangerouslySetInnerHTML={{ __html: underlaySvgDisplay }}
            />
          ) : null}

          {/* Our booth scheme — primary when underlay off; optional ghost over underlay. */}
          {showOurScheme && ourSchemeScene ? (
            <div className={markerToolId ? "pointer-events-none" : undefined}>
              <FloorplanSceneView
                scene={{
                  ...ourSchemeScene,
                  objects: ourSchemeScene.objects.filter((o) => o.kind === "booth"),
                }}
                showBoothCodes={!schemeAsGhost}
                boothTitles={schemeAsGhost ? undefined : boothTitles}
                overlay={schemeAsGhost}
                labelScale={1}
              />
            </div>
          ) : null}

          {!hasUnderlay && !showOurScheme ? (
            <div className="absolute inset-0 flex items-center justify-center border-2 border-dashed border-gray-200 m-2 rounded-xl">
              <div className="text-center space-y-2 p-6">
                <Upload size={28} className="text-gray-200 mx-auto" />
                <div className="text-[13px] text-gray-400">Загрузите .cdr или .svg</div>
                <div className="text-[11px] text-gray-300">
                  1) схема = исходник · 2) расстановка = объекты и экспоненты
                </div>
              </div>
            </div>
          ) : null}

          {cropMode && hasUnderlay ? (
            <div className="absolute inset-0 z-50" data-map-no-pan="">
              {/* Dim trimmed margins of the current view */}
              {cropTrim.top > 0 ? (
                <div
                  className="absolute left-0 right-0 top-0 bg-amber-900/35 pointer-events-none"
                  style={{ height: `${cropTrim.top}%` }}
                />
              ) : null}
              {cropTrim.bottom > 0 ? (
                <div
                  className="absolute left-0 right-0 bottom-0 bg-amber-900/35 pointer-events-none"
                  style={{ height: `${cropTrim.bottom}%` }}
                />
              ) : null}
              {cropTrim.left > 0 ? (
                <div
                  className="absolute top-0 bottom-0 left-0 bg-amber-900/35 pointer-events-none"
                  style={{
                    width: `${cropTrim.left}%`,
                    top: `${cropTrim.top}%`,
                    bottom: `${cropTrim.bottom}%`,
                    height: "auto",
                  }}
                />
              ) : null}
              {cropTrim.right > 0 ? (
                <div
                  className="absolute top-0 bottom-0 right-0 bg-amber-900/35 pointer-events-none"
                  style={{
                    width: `${cropTrim.right}%`,
                    top: `${cropTrim.top}%`,
                    bottom: `${cropTrim.bottom}%`,
                    height: "auto",
                  }}
                />
              ) : null}
              {/* Keep-frame + drag handles (same UX as object resize) */}
              <div
                className="absolute border-2 border-amber-400 shadow-[0_0_0_1px_rgba(245,158,11,0.35)] pointer-events-none"
                style={{
                  left: `${cropTrim.left}%`,
                  top: `${cropTrim.top}%`,
                  right: `${cropTrim.right}%`,
                  bottom: `${cropTrim.bottom}%`,
                }}
              />
              {(
                [
                  { id: "nw" as const, left: cropTrim.left, top: cropTrim.top, cursor: "nwse-resize" },
                  { id: "ne" as const, left: 100 - cropTrim.right, top: cropTrim.top, cursor: "nesw-resize" },
                  { id: "sw" as const, left: cropTrim.left, top: 100 - cropTrim.bottom, cursor: "nesw-resize" },
                  { id: "se" as const, left: 100 - cropTrim.right, top: 100 - cropTrim.bottom, cursor: "nwse-resize" },
                  { id: "n" as const, left: 50, top: cropTrim.top, cursor: "ns-resize" },
                  { id: "s" as const, left: 50, top: 100 - cropTrim.bottom, cursor: "ns-resize" },
                  { id: "w" as const, left: cropTrim.left, top: 50, cursor: "ew-resize" },
                  { id: "e" as const, left: 100 - cropTrim.right, top: 50, cursor: "ew-resize" },
                ] as const
              ).map((h) => (
                <button
                  key={h.id}
                  type="button"
                  data-map-no-pan=""
                  onPointerDown={onCropHandleDown(h.id)}
                  className="absolute z-[60] w-3.5 h-3.5 -ml-1.5 -mt-1.5 rounded-sm bg-amber-500 border-2 border-white shadow-md"
                  style={{ left: `${h.left}%`, top: `${h.top}%`, cursor: h.cursor }}
                  title="Тяните, чтобы обрезать полотно"
                  aria-label={`Обрезка ${h.id}`}
                />
              ))}
            </div>
          ) : null}

          {visibleOverlayMarkers.length > 0 ? (
            <FloorplanOverlayMarkers
              objects={visibleOverlayMarkers}
              selectedId={workMode === "placement" ? selectedMarkerId : null}
              editable={workMode === "placement" && !cropMode}
              stampPassthrough={Boolean(markerToolId)}
              viewZoom={viewZoom}
              labelScale={labelScale}
              boardWidth={board.width}
              boardHeight={board.height}
              boardRotation={boardRotation}
              labelsUpright
              pinLabelRotationDeg={pinLabelRotationDeg}
              onSelect={(mid) => {
                if (workMode !== "placement" || markerToolId) return;
                selectMarker(mid);
              }}
              onMoveStart={onMarkerMoveStart}
              onResizeStart={onMarkerResizeStart}
            />
          ) : null}

          {workMode === "placement" && showPins ? renderPins() : null}
        </div>
      </div>
    </MapViewport>
  );

  const companyRow = (c: Company, i: number) => {
    const color = pinColorFor(c, i);
    const placed = isPlaced(c);
    return (
      <button
        key={c.id}
        type="button"
        onClick={(e) => {
          if (e.shiftKey) {
            selectCompany(c.id, { additive: true });
            setEditMode(true);
            setCropMode(false);
            return;
          }
          if (c.id === selectedCompany) {
            if (isPlaced(c)) focusOnBoardPct(c.mapPoint.x, c.mapPoint.y);
            else clearCompanySelection();
            return;
          }
          selectCompany(c.id);
          setEditMode(true);
          setCropMode(false);
        }}
        className={`w-full flex items-center gap-2.5 px-3 py-2 text-left ${
          selectedCompany === c.id || selectedIds.includes(c.id)
            ? "bg-indigo-50"
            : "hover:bg-gray-50"
        }`}
      >
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 border-2 border-white shadow"
          style={{ backgroundColor: color }}
        >
          {c.logoUrl ? (
            <img src={c.logoUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-white text-[9px] font-semibold">
              {(c.name || "?").slice(0, 2).toUpperCase()}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[12px] text-gray-900 truncate">{c.name}</div>
          <div className="text-[10px] text-gray-400 truncate">
            {c.booth ? `Стенд ${c.booth}` : "Без номера"}
            {placed ? " · на карте" : " · не размещён"}
            {c.mapLocked ? " · закреплён" : ""}
          </div>
        </div>
        {c.mapLocked ? (
          <Lock size={12} className="text-slate-500 flex-shrink-0" />
        ) : (
          <span
            className={`w-2 h-2 rounded-full flex-shrink-0 ${placed ? "bg-emerald-400" : "bg-gray-300"}`}
          />
        )}
      </button>
    );
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div
        className="flex items-center gap-2 px-3 sm:px-4 py-2.5 bg-white border-b border-gray-100 flex-shrink-0 overflow-x-auto"
        style={{ scrollbarWidth: "none" }}
      >
        <button
          type="button"
          onClick={() => navigate(`/events/${id}`)}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 flex-shrink-0 hidden sm:block"
        >
          <ArrowLeft size={16} />
        </button>
        <h1 className="text-[14px] text-gray-900 mr-1 flex-shrink-0 hidden sm:block">Карта</h1>

        {halls.length > 1 && (
          <div className="flex items-center gap-1 bg-gray-50 rounded-lg p-0.5 flex-shrink-0">
            {halls.map((hall) => (
              <button
                key={hall.id}
                type="button"
                onClick={() => setActiveHall(hall.id)}
                className={`px-2 py-1 rounded-md text-[11px] ${
                  activeHall === hall.id ? "bg-white shadow text-indigo-600" : "text-gray-500"
                }`}
              >
                {hall.name || hall.label}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5 flex-shrink-0 ml-1">
          <button
            type="button"
            onClick={() => switchWorkMode("scheme")}
            className={`px-2.5 py-1 rounded-md text-[11px] font-medium ${
              workMode === "scheme" ? "bg-white shadow text-slate-900" : "text-slate-500"
            }`}
            title="Схема = исходник: загрузка / обрезка / слои подложки"
          >
            Схема
          </button>
          <button
            type="button"
            onClick={() => switchWorkMode("placement")}
            className={`px-2.5 py-1 rounded-md text-[11px] font-medium ${
              workMode === "placement" ? "bg-white shadow text-indigo-700" : "text-slate-500"
            }`}
            title="Расстановка: экспоненты и объекты (офис, вход, туалет…) поверх схемы"
          >
            Расстановка
          </button>
        </div>

        <div className="flex items-center gap-1.5 ml-auto flex-shrink-0">
          {!isDesktop && workMode === "placement" && (
            <button
              type="button"
              onClick={() => setMobileListOpen(true)}
              className="p-1.5 rounded-lg border border-gray-200 text-gray-600"
            >
              <List size={14} />
            </button>
          )}

          {workMode === "placement" ? (
          <div className="relative hidden sm:block">
            <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 w-40">
              <Search size={12} className="text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Стенд…"
                className="flex-1 text-base bg-transparent outline-none text-gray-700 placeholder-gray-400 w-0"
              />
            </div>
            {searchFiltered.length > 0 && (
              <div className="absolute top-full mt-1 left-0 right-0 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-20 max-h-48 overflow-y-auto">
                {searchFiltered.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      selectCompany(c.id);
                      setSearch("");
                      setEditMode(true);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-left"
                  >
                    <MapPin size={12} className="text-indigo-400 flex-shrink-0" />
                    <div>
                      <div className="text-[12px] text-gray-900">{c.name}</div>
                      <div className="text-[10px] text-gray-400">
                        {c.booth || "—"} · {c.hall || "—"}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          ) : null}

          {hasPlan && (
            <div
              className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-lg px-1.5 py-1"
              title="Масштаб рабочей области (приблизить / отдалить план)"
            >
              <Maximize2 size={12} className="text-gray-400 flex-shrink-0 ml-0.5" />
              <button
                type="button"
                className="px-1.5 py-0.5 text-[12px] text-gray-600 hover:bg-white rounded"
                title="Отдалить"
                onClick={() => {
                  const fitZ = mapViewportRef.current?.getFitZoom() ?? viewZoom;
                  const nextPct = Math.max(25, viewZoomPct - 10);
                  setViewZoomPct(nextPct);
                  mapViewportRef.current?.setZoomCentered(fitZ * (nextPct / 100));
                }}
              >
                −
              </button>
              <input
                type="range"
                min={25}
                max={400}
                step={5}
                value={viewZoomPct}
                onChange={(e) => {
                  const nextPct = Number(e.target.value);
                  setViewZoomPct(nextPct);
                  const fitZ = mapViewportRef.current?.getFitZoom() ?? viewZoom;
                  mapViewportRef.current?.setZoomCentered(fitZ * (nextPct / 100));
                }}
                className="w-14 sm:w-20 accent-indigo-500"
              />
              <button
                type="button"
                className="px-1.5 py-0.5 text-[12px] text-gray-600 hover:bg-white rounded"
                title="Приблизить"
                onClick={() => {
                  const fitZ = mapViewportRef.current?.getFitZoom() ?? viewZoom;
                  const nextPct = Math.min(400, viewZoomPct + 10);
                  setViewZoomPct(nextPct);
                  mapViewportRef.current?.setZoomCentered(fitZ * (nextPct / 100));
                }}
              >
                +
              </button>
              <button
                type="button"
                className="px-1 py-0.5 text-[10px] text-indigo-600 hover:bg-white rounded tabular-nums"
                title="Вписать план по ширине (100%)"
                onClick={() => {
                  setViewZoomPct(100);
                  mapViewportRef.current?.fit();
                }}
              >
                {viewZoomPct}%
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Row: actions */}
      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border-b border-gray-100 flex-shrink-0 overflow-x-auto">
        <span className="text-[10px] uppercase tracking-wider text-slate-400 flex-shrink-0 mr-0.5">
          Действия
        </span>
        <input
          ref={svgInputRef}
          type="file"
          accept=".png,.jpg,.jpeg,.webp,.svg,.cdr,image/png,image/jpeg,image/webp,image/svg+xml,application/x-vnd.corel.zcf.draw.document+zip"
          className="hidden"
          onChange={(e) => void handleFloorplanUpload(e.target.files?.[0] ?? null)}
        />
        {workMode === "scheme" ? (
          <>
            <button
              type="button"
              onClick={() => svgInputRef.current?.click()}
              disabled={uploadingSvg || !activeHallObj}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-700 text-[12px] disabled:opacity-50"
              title="PNG / JPG / SVG / CDR — исходник (подложка). Объекты — во вкладке Расстановка"
            >
              {uploadingSvg ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
              <span>Загрузить</span>
            </button>
            <button
              type="button"
              onClick={() => {
                const next = !cropMode;
                setCropMode(next);
                setCropTrim({ left: 0, top: 0, right: 0, bottom: 0 });
                setMarkerToolId(null);
                if (next) {
                  setEditMode(false);
                  setPlaceMode(false);
                }
              }}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] ${
                cropMode ? "bg-amber-500 text-white" : "border border-gray-200 text-gray-600"
              }`}
              title="Обрезать полотно — тяните углы рамки на схеме"
            >
              <Crop size={13} />
              <span className="hidden sm:inline">Обрезка</span>
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => {
                setEditMode(!editMode);
                setCropMode(false);
                if (editMode) setPlaceMode(false);
              }}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] ${
                editMode ? "bg-amber-500 text-white" : "border border-gray-200 text-gray-600"
              }`}
            >
              <Move size={13} />
              <span>{editMode ? "Редакт." : "Редактировать"}</span>
            </button>
            <button
              type="button"
              onClick={() => void autoPlaceByBoothNumbers()}
              disabled={!hasPlan || !hallCompanies.length}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 text-[12px] disabled:opacity-50"
            >
              <Sparkles size={13} />
              <span>Авторасстановка</span>
            </button>
            <button
              type="button"
              onClick={() =>
                void removePinsFromMap(
                  selectedIds.length
                    ? selectedIds
                    : selectedCompany
                      ? [selectedCompany]
                      : placedOnHall.map((c) => c.id),
                )
              }
              disabled={!selectedIds.length && !selectedCompany && !placedOnHall.length}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-red-200 bg-red-50 text-red-700 text-[12px] disabled:opacity-50"
              title="Убрать с карты (Delete)"
            >
              <Trash2 size={13} />
              <span className="hidden sm:inline">
                {selectedIds.length > 1 ? `Убрать (${selectedIds.length})` : "Убрать"}
              </span>
            </button>
          </>
        )}

        <div className="w-px h-5 bg-gray-200 flex-shrink-0 mx-0.5" />

        <div className="relative flex-shrink-0">
          <button
            ref={layersBtnRef}
            type="button"
            onClick={() => openLayersMenu()}
            className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[12px] ${
              layersOpen ? "bg-slate-800 text-white" : "border border-gray-200 text-gray-700"
            }`}
            title="Слои карты"
          >
            <Layers size={13} />
            <span className="hidden md:inline">Слои</span>
            {!showUnderlay || !showPins || !showOverlay ? (
              <EyeOff size={11} className="opacity-70" />
            ) : (
              <Eye size={11} className="opacity-70" />
            )}
          </button>
          {layersOpen && layersMenuPos ? (
            <>
              <div
                className="fixed inset-0 z-[80] cursor-default"
                onClick={() => {
                  setLayersOpen(false);
                  setLayersMenuPos(null);
                }}
              />
              <div
                className="fixed z-[90] w-72 max-h-[70vh] overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-xl py-2 text-[12px]"
                style={{ top: layersMenuPos.top, right: layersMenuPos.right }}
              >
                <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                  Карта
                </div>
                <label className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-slate-800"
                    checked={showUnderlay && hasUnderlay}
                    disabled={!hasUnderlay}
                    onChange={() => toggleUnderlay()}
                  />
                  <span className="flex-1">Подложка Corel</span>
                  <span className="text-[10px] text-gray-400">в приложении</span>
                </label>
                <label className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-indigo-600"
                    checked={showSchemeBooths}
                    disabled={boothMarkerCount === 0 && !ourSchemeScene}
                    onChange={() => setShowSchemeBooths((v) => !v)}
                  />
                  <span className="flex-1">Маркеры-стенды (устаревшие)</span>
                  <span className="text-[10px] text-gray-400">{boothMarkerCount}</span>
                </label>
                <label className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-indigo-600"
                    checked={showOverlay}
                    disabled={
                      !schemeMarkers.some((o) => o.kind !== "booth") && !hasScene
                    }
                    onChange={() => setShowOverlay((v) => !v)}
                  />
                  <span className="flex-1">Маркеры (вход, W/C…)</span>
                  <span className="text-[10px] text-gray-400">
                    {schemeMarkers.filter((o) => o.kind !== "booth").length}
                  </span>
                </label>
                <label className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-indigo-600"
                    checked={showPins}
                    disabled={workMode !== "placement"}
                    onChange={() => setShowPins((v) => !v)}
                  />
                  <span className="flex-1">Экспоненты (пины)</span>
                  <span className="text-[10px] text-gray-400">
                    {workMode === "placement" ? "объекты" : "только в расстановке"}
                  </span>
                </label>
                <div className="mt-1 border-t border-gray-100 px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                  Внутри подложки
                </div>
                <div className="px-3 pb-1 text-[10px] text-gray-400 leading-snug">
                  Имена слоёв Corel при экспорте не сохраняются — режем по типу фигур.
                </div>
                {(
                  [
                    { id: "fills" as const, label: "Заливки стендов" },
                    { id: "texts" as const, label: "Надписи Corel (справка кто где)" },
                    { id: "grid" as const, label: "Сетка" },
                    { id: "contours" as const, label: "Чёрные контуры" },
                    { id: "decor" as const, label: "Декор (полосы, стрелки)" },
                  ] as const
                ).map((row) => {
                  const on = resolveUnderlaySvgLayers(underlayLayers)[row.id];
                  return (
                    <label
                      key={row.id}
                      className={`flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer ${
                        !showUnderlay || !hasUnderlay ? "opacity-40 pointer-events-none" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="accent-amber-600"
                        checked={on}
                        disabled={!showUnderlay || !hasUnderlay}
                        onChange={() => toggleUnderlayLayer(row.id)}
                      />
                      <span className="flex-1">{row.label}</span>
                    </label>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => {
            if (!labelsUpright) {
              setLabelsUpright(true);
              setPinLabelRotationDeg(0);
              patchSceneMeta({ labelsUpright: true, pinLabelRotationDeg: 0 });
              return;
            }
            // Already on: if global twist is non-zero, straighten (don't turn off).
            if (pinLabelRotationDeg !== 0) {
              setPinLabelRotationDeg(0);
              patchSceneMeta({ labelsUpright: true, pinLabelRotationDeg: 0 });
              return;
            }
            setLabelsUpright(false);
            patchSceneMeta({ labelsUpright: false });
          }}
          className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[12px] ${
            labelsUpright ? "bg-emerald-600 text-white" : "border border-gray-200 text-gray-600"
          }`}
          title="Держать надписи горизонтально на экране (сбрасывает общий поворот надписей)"
        >
          <PenLine size={13} />
          <span className="hidden lg:inline">Надписи прямо</span>
        </button>
        <button
          type="button"
          onClick={() => rotatePinLabelsGlobal(-1)}
          className="flex items-center px-2 py-1.5 rounded-lg border border-gray-200 text-gray-600"
          title="Повернуть все надписи расстановки на 90° влево"
        >
          <RotateCcw size={13} />
        </button>
        <button
          type="button"
          onClick={() => rotatePinLabelsGlobal(1)}
          className="flex items-center px-2 py-1.5 rounded-lg border border-gray-200 text-gray-600"
          title="Повернуть все надписи расстановки на 90° вправо"
        >
          <RotateCw size={13} />
        </button>
        <span
          className="text-[10px] tabular-nums text-slate-500 w-8 text-center flex-shrink-0"
          title="Глобальный поворот надписей расстановки"
        >
          {pinLabelRotationDeg}°
        </span>
        <label
          className="flex items-center gap-1 px-1.5 py-1 rounded-lg border border-gray-200 text-[11px] text-gray-600 flex-shrink-0"
          title="Только размер шрифта на пинах (не масштаб карты)"
        >
          <span className="hidden sm:inline whitespace-nowrap">Шрифт</span>
          <input
            type="range"
            min={0.7}
            max={2.2}
            step={0.05}
            value={labelScale}
            onChange={(e) => changeLabelScale(Number(e.target.value))}
            className="w-14 accent-indigo-500"
          />
          <span className="tabular-nums w-7">{Math.round(labelScale * 100)}%</span>
        </label>
        <button
          type="button"
          onClick={() => undoScene()}
          disabled={undoDepth === 0}
          className="flex items-center px-2 py-1.5 rounded-lg border border-gray-200 text-gray-600 disabled:opacity-40"
          title="Шаг назад (Ctrl+Z)"
        >
          <Undo2 size={13} />
        </button>
        <button
          type="button"
          onClick={() => redoScene()}
          disabled={redoDepth === 0}
          className="flex items-center px-2 py-1.5 rounded-lg border border-gray-200 text-gray-600 disabled:opacity-40"
          title="Шаг вперёд (Ctrl+Y)"
        >
          <Redo2 size={13} />
        </button>
        <button
          type="button"
          onClick={() => void saveSceneExplicit()}
          disabled={!sceneDirty || !sceneDraft}
          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[12px] font-medium disabled:opacity-40 ${
            sceneDirty
              ? "bg-indigo-600 text-white shadow-sm"
              : "border border-gray-200 text-gray-500"
          }`}
          title="Сохранить схему (Ctrl+S)"
        >
          <Save size={13} />
          <span className="hidden sm:inline">{sceneDirty ? "Сохранить" : "Сохранено"}</span>
        </button>
        <button
          type="button"
          onClick={() => void rotatePlan(-1)}
          disabled={uploadingSvg || !hasPlan}
          className="flex items-center px-2 py-1.5 rounded-lg border border-gray-200 text-gray-600 disabled:opacity-50"
          title="Повернуть план 90° влево"
        >
          <RotateCcw size={13} />
        </button>
        <button
          type="button"
          onClick={() => void rotatePlan(1)}
          disabled={uploadingSvg || !hasPlan}
          className="flex items-center px-2 py-1.5 rounded-lg border border-gray-200 text-gray-600 disabled:opacity-50"
          title="Повернуть план 90° вправо"
        >
          <RotateCw size={13} />
        </button>

        <div className="flex-shrink-0 ml-auto">
          <button
            ref={moreBtnRef}
            type="button"
            onClick={() => openMoreMenu()}
            className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-[12px] ${
              moreOpen ? "bg-slate-200 text-slate-800" : "border border-gray-200 text-gray-600"
            }`}
            title="Ещё действия"
          >
            <MoreHorizontal size={14} />
            <span className="hidden sm:inline">Ещё</span>
          </button>
          {moreOpen && moreMenuPos ? (
            <>
              <button
                type="button"
                className="fixed inset-0 z-[80] cursor-default"
                aria-label="Закрыть меню"
                onClick={() => {
                  setMoreOpen(false);
                  setMoreMenuPos(null);
                }}
              />
              <div
                className="fixed z-[90] w-56 rounded-xl border border-gray-100 bg-white shadow-xl py-1 text-[12px]"
                style={{ top: moreMenuPos.top, right: moreMenuPos.right }}
              >
                {workMode === "placement" ? (
                  <>
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-left hover:bg-gray-50 disabled:opacity-40"
                      disabled={!placedOnHall.length}
                      onClick={() => {
                        setMoreOpen(false);
                        setMoreMenuPos(null);
                        setEditMode(true);
                        const ids = placedOnHall.map((c) => c.id);
                        setSelectedIds(ids);
                        setSelectedCompany(ids[ids.length - 1] ?? null);
                      }}
                    >
                      Выделить все пины
                    </button>
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-left hover:bg-gray-50"
                      onClick={() => {
                        setMoreOpen(false);
                        setMoreMenuPos(null);
                        setCropMode(true);
                        setEditMode(false);
                      }}
                    >
                      Обрезка полей
                    </button>
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-left hover:bg-gray-50 disabled:opacity-40"
                      disabled={uploadingSvg || !activeHallObj}
                      onClick={() => {
                        setMoreOpen(false);
                        setMoreMenuPos(null);
                        svgInputRef.current?.click();
                      }}
                    >
                      Загрузить подложку
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-left hover:bg-gray-50 disabled:opacity-40"
                      disabled={!selectedMarkerId}
                      onClick={() => {
                        setMoreOpen(false);
                        setMoreMenuPos(null);
                        void removeSelectedMarker();
                      }}
                    >
                      Удалить маркер
                    </button>
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-left hover:bg-gray-50 disabled:opacity-40"
                      disabled={!schemeMarkers.length}
                      onClick={() => {
                        setMoreOpen(false);
                        setMoreMenuPos(null);
                        void clearSchemeMarkers();
                      }}
                    >
                      Очистить все маркеры
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left hover:bg-gray-50"
                  onClick={() => {
                    setMoreOpen(false);
                    setMoreMenuPos(null);
                    toggleUnderlay();
                  }}
                >
                  {showUnderlay ? "Скрыть подложку в приложении" : "Показать подложку в приложении"}
                </button>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 disabled:opacity-40"
                  disabled={uploadingSvg || !hasPlan}
                  onClick={() => {
                    setMoreOpen(false);
                    setMoreMenuPos(null);
                    void fitPlan();
                  }}
                >
                  По содержимому
                </button>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-red-600 hover:bg-red-50 disabled:opacity-40"
                  disabled={uploadingSvg || !hasPlan}
                  onClick={() => {
                    setMoreOpen(false);
                    setMoreMenuPos(null);
                    void clearPlan();
                  }}
                >
                  Удалить схему
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* Row: elements — placement stamps on top of underlay scheme */}
      {workMode === "placement" ? (
        <div className="flex flex-col gap-1.5 px-3 py-1.5 bg-slate-50 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-1.5 overflow-x-auto">
            <span className="text-[10px] uppercase tracking-wider text-slate-400 flex-shrink-0 mr-0.5">
              Элементы
            </span>
            {MARKER_TOOLS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  setCropMode(false);
                  setPlaceMode(false);
                  clearCompanySelection();
                  setSelectedMarkerId(null);
                  setMarkerLabelDraft("");
                  setMarkerCodeDraft("");
                  setMarkerToolId((prev) => (prev === t.id ? null : t.id));
                }}
                className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] flex-shrink-0 ${
                  markerToolId === t.id
                    ? "bg-indigo-600 text-white"
                    : t.id === "office"
                      ? "bg-amber-50 border border-amber-300 text-amber-900"
                      : "bg-white border border-gray-200 text-gray-600"
                }`}
                title={`Тех. объект «${t.label}» — клик по карте`}
                style={
                  markerToolId === t.id
                    ? undefined
                    : { borderColor: FLOORPLAN_KIND_STYLE[t.kind]?.stroke }
                }
              >
                {t.kind === "entrance" ? (
                  <DoorOpen size={12} />
                ) : t.kind === "exit" ? (
                  <LogOut size={12} />
                ) : t.kind === "stairs" ? (
                  <ArrowUpDown size={12} />
                ) : t.kind === "info" ? (
                  <Info size={12} />
                ) : t.kind === "service" ? (
                  <Coffee size={12} />
                ) : t.kind === "event_link" ? (
                  <ArrowRight size={12} />
                ) : (
                  <PenLine size={12} />
                )}
                <span>{t.kind === "event_link" ? "Переход" : t.label}</span>
              </button>
            ))}
            {boothMarkerCount > 0 ? (
              <button
                type="button"
                onClick={() => void clearBoothMarkers()}
                disabled={uploadingSvg}
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-[11px] disabled:opacity-50 ml-1 flex-shrink-0"
                title="Убрать дублирующие маркеры-стенды — экспоненты остаются пинами"
              >
                <Trash2 size={12} />
                Убрать маркеры-стенды ({boothMarkerCount})
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => void removeSelectedMarker()}
              disabled={!selectedMarkerId}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg border border-red-200 bg-red-50 text-red-700 text-[11px] disabled:opacity-50 ml-1 flex-shrink-0"
              title="Удалить выделенный (Delete)"
            >
              <Trash2 size={12} />
            </button>
            <button
              type="button"
              onClick={() => toggleSelectedMarkerLock()}
              disabled={!selectedMarkerId}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-lg border text-[11px] disabled:opacity-50 flex-shrink-0 ${
                selectedMarker?.locked
                  ? "border-slate-600 bg-slate-700 text-white"
                  : "border-slate-200 bg-white text-slate-700"
              }`}
              title={selectedMarker?.locked ? "Открепить объект" : "Закрепить объект"}
            >
              {selectedMarker?.locked ? <Unlock size={12} /> : <Lock size={12} />}
              {selectedMarker?.locked ? "Открепить" : "Закрепить"}
            </button>
            <button
              type="button"
              onClick={() => snapSelectedMarkerToBooth()}
              disabled={!selectedMarkerId || selectedMarker?.locked === true}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-[11px] disabled:opacity-50 flex-shrink-0"
              title="Подогнать по стенду на подложке"
            >
              <Shrink size={12} />
              По подложке
            </button>
            <span className="text-[10px] uppercase tracking-wider text-slate-400 flex-shrink-0 ml-2">
              Поле
            </span>
            {(
              [
                ["n", "↑"],
                ["s", "↓"],
                ["w", "←"],
                ["e", "→"],
              ] as const
            ).map(([edge, glyph]) => (
              <button
                key={edge}
                type="button"
                disabled={uploadingSvg || !hasPlan}
                onClick={() => void expandBoardEdge(edge, 10)}
                className="px-2 py-1.5 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-800 text-[11px] font-medium disabled:opacity-50 flex-shrink-0"
                title={`Освободить ~10% ${
                  edge === "n"
                    ? "сверху"
                    : edge === "s"
                      ? "снизу"
                      : edge === "w"
                        ? "слева"
                        : "справа"
                } под плашки «Переход» (стенды сдвинутся)`}
              >
                {glyph} +10%
              </button>
            ))}
            {markerToolId && (
              <span className="text-[11px] text-indigo-700 ml-1 whitespace-nowrap font-medium">
                Кликните на карту → «{MARKER_TOOLS.find((t) => t.id === markerToolId)?.label}»
                {" "}(клик проходит сквозь стенды)
              </span>
            )}
          </div>
          {selectedMarker ? (
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-700">
              <span className="text-slate-400 uppercase tracking-wide">Размер</span>
              <label className="flex items-center gap-1">
                Ш
                <input
                  type="number"
                  min={1.2}
                  max={100}
                  step={0.1}
                  disabled={selectedMarker.locked === true}
                  value={selectedMarker.w}
                  onChange={(e) => updateSelectedMarkerSize("w", Number(e.target.value))}
                  className="w-14 px-1 py-0.5 rounded border border-slate-200 bg-white tabular-nums disabled:bg-slate-100"
                />
              </label>
              <label className="flex items-center gap-1">
                В
                <input
                  type="number"
                  min={1.2}
                  max={100}
                  step={0.1}
                  disabled={selectedMarker.locked === true}
                  value={selectedMarker.h}
                  onChange={(e) => updateSelectedMarkerSize("h", Number(e.target.value))}
                  className="w-14 px-1 py-0.5 rounded border border-slate-200 bg-white tabular-nums disabled:bg-slate-100"
                />
              </label>
              <label className="flex items-center gap-1 ml-1">
                Шрифт
                <input
                  type="range"
                  min={50}
                  max={250}
                  step={5}
                  value={Math.round((selectedMarker.fontScale ?? 1) * 100)}
                  onChange={(e) => {
                    const base = sceneDraftRef.current;
                    if (!base || !selectedMarkerId) return;
                    const fontScale = Number(e.target.value) / 100;
                    commitSceneLocal(
                      {
                        ...base,
                        objects: base.objects.map((o) =>
                          o.id === selectedMarkerId ? { ...o, fontScale } : o,
                        ),
                      },
                      { recordUndo: false },
                    );
                  }}
                  className="w-20 accent-indigo-600"
                  title="Размер текста объекта"
                />
                <span className="tabular-nums w-8">
                  {Math.round((selectedMarker.fontScale ?? 1) * 100)}%
                </span>
              </label>
              <span className="flex items-center gap-1 ml-1">
                <span className="text-slate-400">Надпись</span>
                <button
                  type="button"
                  onClick={() => rotateSelectedMarkerLabel(-1)}
                  className="flex items-center justify-center w-7 h-7 rounded-md border border-slate-200 text-slate-600 hover:bg-white"
                  title="Надпись −90°"
                >
                  <RotateCcw size={12} />
                </button>
                <span className="tabular-nums w-8 text-center text-[11px] font-medium">
                  {normalizePinLabelRot(selectedMarker.labelRot)}°
                </span>
                <button
                  type="button"
                  onClick={() => rotateSelectedMarkerLabel(1)}
                  className="flex items-center justify-center w-7 h-7 rounded-md border border-slate-200 text-slate-600 hover:bg-white"
                  title="Надпись +90°"
                >
                  <RotateCw size={12} />
                </button>
              </span>
              <span className="text-slate-400">
                {selectedMarker.locked ? "закреплён" : "текст по центру"}
              </span>
            </div>
          ) : null}
          {selectedMarker?.kind === "booth" ? (
            <div className="flex flex-wrap items-start gap-2">
              <label className="flex items-center gap-1.5 text-[11px] text-slate-700">
                Номер:
                <input
                  value={markerCodeDraft}
                  onChange={(e) => updateSelectedMarkerCode(e.target.value)}
                  className="w-20 px-1.5 py-0.5 rounded border border-slate-200 bg-white font-mono"
                  placeholder="A413"
                />
              </label>
              <label className="flex items-start gap-1.5 text-[11px] text-slate-700 flex-1 min-w-[12rem]">
                Компании
                <span className="text-slate-400">(по строке)</span>:
                <textarea
                  value={markerLabelDraft}
                  onChange={(e) => updateSelectedMarkerLabel(e.target.value)}
                  rows={2}
                  className="flex-1 min-w-0 px-1.5 py-0.5 rounded border border-slate-200 bg-white resize-y"
                  placeholder={"КЕСТО\nАРБОР НОВА"}
                />
              </label>
              <label className="flex items-center gap-1.5 text-[11px] text-slate-700">
                <input
                  type="checkbox"
                  className="accent-indigo-600"
                  checked={selectedMarker.showInApp !== false}
                  onChange={() => toggleSelectedMarkerShowInApp()}
                />
                В приложении
              </label>
            </div>
          ) : selectedMarkerId && selectedMarker ? (
            <div className="flex flex-wrap items-center gap-3">
              {selectedMarker.kind === "event_link" ? (
                <>
                  <label className="flex items-center gap-1.5 text-[11px] text-slate-700">
                    Выставка:
                    <select
                      value={selectedMarker.linkEventId ?? ""}
                      onChange={(e) => updateSelectedEventLink(e.target.value)}
                      className="max-w-[16rem] px-1.5 py-0.5 rounded border border-slate-200 bg-white"
                    >
                      <option value="">— выбрать из группы —</option>
                      {groupSiblings.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.title}
                        </option>
                      ))}
                    </select>
                  </label>
                  {!groupSiblings.length ? (
                    <span className="text-[11px] text-amber-700">
                      Выставка не в группе — добавьте в «Группы выставок»
                    </span>
                  ) : null}
                  <label className="flex items-center gap-1.5 text-[11px] text-slate-700">
                    Стрелка:
                    <select
                      value={selectedMarker.linkArrow ?? "e"}
                      onChange={(e) =>
                        updateSelectedLinkArrow(e.target.value as FloorplanLinkArrow)
                      }
                      className="px-1.5 py-0.5 rounded border border-slate-200 bg-white"
                    >
                      <option value="e">→ вправо</option>
                      <option value="w">← влево</option>
                      <option value="n">↑ вверх</option>
                      <option value="s">↓ вниз</option>
                    </select>
                  </label>
                </>
              ) : null}
              <label className="flex items-center gap-1.5 text-[11px] text-slate-700">
                Текст:
                <input
                  value={markerLabelDraft}
                  onChange={(e) => updateSelectedMarkerLabel(e.target.value)}
                  className="w-48 px-1.5 py-0.5 rounded border border-slate-200 bg-white"
                  placeholder={
                    selectedMarker.kind === "event_link" ? "Название →" : "Подпись"
                  }
                />
              </label>
              <label className="flex items-center gap-1.5 text-[11px] text-slate-700">
                <input
                  type="checkbox"
                  className="accent-indigo-600"
                  checked={selectedMarker.showInApp !== false}
                  onChange={() => toggleSelectedMarkerShowInApp()}
                />
                Показывать в приложении
              </label>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="px-3 py-1 bg-slate-50 border-b border-slate-100 text-[11px] text-slate-500 flex-shrink-0">
          Схема = исходник (подложка). Объекты и экспоненты — во вкладке «Расстановка»
          {cropMode ? " · обрезка полей" : ""}
        </div>
      )}

      {cropMode && (
        <div className="px-3 py-2 bg-amber-50 border-b border-amber-100 flex flex-wrap gap-2 items-center flex-shrink-0">
          <span className="text-[11px] text-amber-900">
            Обрезка только уменьшает поле. Чтобы добавить место сверху/сбоку под «Переход» —
            выйдите из обрезки и нажмите «↑ +10%» в ряду «Поле». Тяните края рамки, чтобы
            обрезать лишнее; координаты стендов пересчитаются сами.
          </span>
          <span className="text-[10px] text-amber-800/80 tabular-nums">
            trim L{cropTrim.left} T{cropTrim.top} R{cropTrim.right} B{cropTrim.bottom} · base L
            {cropDraft.left} T{cropDraft.top} R{cropDraft.right} B{cropDraft.bottom}
          </span>
          <button
            type="button"
            onClick={() => {
              setCropTrim({ left: 0, top: 0, right: 0, bottom: 0 });
              void persistCrop(
                { left: 0, top: 0, right: 0, bottom: 0 },
                cropDraftRef.current,
              );
            }}
            className="px-2 py-1 rounded-md text-[11px] bg-white border border-amber-200 text-amber-800"
          >
            Сбросить
          </button>
          <button
            type="button"
            onClick={() => void applyCropTrim()}
            className="px-2.5 py-1 rounded-md text-[11px] bg-amber-500 text-white"
          >
            Применить
          </button>
          <button
            type="button"
            onClick={() => {
              setCropMode(false);
              setCropTrim({ left: 0, top: 0, right: 0, bottom: 0 });
            }}
            className="px-2 py-1 rounded-md text-[11px] text-amber-900/80"
          >
            Готово
          </button>
        </div>
      )}

      {saveError && (
        <div
          className={`px-3 py-1.5 border-b text-[12px] flex-shrink-0 ${
            saveError.startsWith("Расставлено") ||
            saveError.startsWith("Подложка") ||
            saveError.startsWith("Схема подогнана") ||
            saveError.startsWith("Стенды →") ||
            saveError.startsWith("Схема сохранена") ||
            saveError.startsWith("Выделено")
              ? "bg-emerald-50 border-emerald-100 text-emerald-700"
              : "bg-red-50 border-red-100 text-red-600"
          }`}
        >
          {saveError}
        </div>
      )}

      {isDesktop ? (
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {workMode === "scheme" ? (
            <div className="w-56 bg-white border-r border-gray-100 flex flex-col flex-shrink-0">
              <div className="px-3 pt-3 pb-1.5 text-[11px] text-gray-400 uppercase tracking-wider">
                Исходник
              </div>
              <div className="flex-1 overflow-y-auto px-3 py-4 text-[12px] text-slate-500 space-y-3">
                <p className="leading-relaxed">
                  Здесь только подложка: загрузка, обрезка, слои Corel.
                </p>
                <p className="leading-relaxed text-slate-400">
                  Офис, вход, туалет, экспоненты — во вкладке{" "}
                  <b className="text-slate-600">Расстановка</b>.
                </p>
                {hasUnderlay ? (
                  <p className="text-[11px] text-emerald-600">Подложка загружена</p>
                ) : (
                  <p className="text-[11px] text-amber-600">Загрузите PNG / SVG / CDR</p>
                )}
              </div>
              {hasPlan ? (
                <div className="p-2 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => switchWorkMode("placement")}
                    className="w-full py-2 rounded-lg bg-indigo-600 text-white text-[12px] font-medium"
                  >
                    Дальше: расстановка →
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="w-56 bg-white border-r border-gray-100 flex flex-col flex-shrink-0">
              <div className="px-3 pt-2 pb-1 text-[11px] text-gray-400 uppercase tracking-wider">
                Тех. объекты · {techMarkers.length}
              </div>
              <div className="max-h-36 overflow-y-auto border-b border-gray-50">
                {techMarkers.length === 0 ? (
                  <div className="px-3 py-3 text-[11px] text-gray-400 text-center leading-snug">
                    Офис, вход, туалет… — штамп сверху + клик
                  </div>
                ) : (
                  techMarkers.map((o) => {
                    const style = FLOORPLAN_KIND_STYLE[o.kind] ?? FLOORPLAN_KIND_STYLE.other;
                    const selected = selectedMarkerId === o.id;
                    return (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => selectMarker(o.id)}
                        className={`w-full flex items-center gap-2 px-3 py-1.5 text-left ${
                          selected ? "bg-indigo-50" : "hover:bg-gray-50"
                        }`}
                        title="Клик — найти на карте"
                      >
                        <span
                          className="w-2 h-2 rounded-sm flex-shrink-0"
                          style={{ backgroundColor: style.stroke }}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="text-[11px] text-gray-900 truncate">
                            {o.label || o.kind}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
              <div className="px-3 pt-2 pb-1.5 text-[11px] text-gray-400 uppercase tracking-wider">
                Экспоненты · {listFiltered.length}
                {listSearch.trim() ? ` / ${hallCompanies.length}` : ""}
              </div>
              <div className="px-2 pb-2">
                <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5">
                  <Search size={12} className="text-gray-400 flex-shrink-0" />
                  <input
                    value={listSearch}
                    onChange={(e) => setListSearch(e.target.value)}
                    placeholder="Компания или стенд…"
                    className="flex-1 min-w-0 text-[12px] bg-transparent outline-none text-gray-700 placeholder-gray-400"
                  />
                  {listSearch ? (
                    <button
                      type="button"
                      onClick={() => setListSearch("")}
                      className="p-0.5 text-gray-400 hover:text-gray-600"
                      aria-label="Очистить"
                    >
                      <X size={12} />
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {listFiltered.length === 0 ? (
                  <div className="px-3 py-6 text-[12px] text-gray-400 text-center">Ничего не найдено</div>
                ) : (
                  listFiltered.map((c, i) => companyRow(c, i))
                )}
              </div>
            </div>
          )}

          {mapCanvas}
          {workMode === "placement" ? detailPanel : null}
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden relative">
          {mapCanvas}
          {selectedComp && (
            <div className="absolute inset-x-0 bottom-0 z-20 max-h-[45vh] overflow-hidden shadow-2xl rounded-t-2xl">
              {detailPanel}
            </div>
          )}
          {mobileListOpen && (
            <div className="absolute inset-0 z-30 flex flex-col justify-end">
              <div className="absolute inset-0 bg-black/30" onClick={() => setMobileListOpen(false)} />
              <div className="relative bg-white rounded-t-2xl max-h-[60vh] overflow-hidden shadow-2xl flex flex-col">
                <div className="px-3 pt-3 pb-2 border-b border-gray-50">
                  <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5">
                    <Search size={12} className="text-gray-400 flex-shrink-0" />
                    <input
                      value={listSearch}
                      onChange={(e) => setListSearch(e.target.value)}
                      placeholder="Компания или стенд…"
                      className="flex-1 min-w-0 text-[12px] bg-transparent outline-none text-gray-700 placeholder-gray-400"
                    />
                  </div>
                </div>
                <div className="overflow-y-auto flex-1">
                  {listFiltered.map((c, i) => (
                    <div key={c.id} onClick={() => setMobileListOpen(false)}>
                      {companyRow(c, i)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
